import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Row, Col, Card, Button, Table, ListGroup, Modal, Form, Alert, Badge, Spinner, Dropdown, DropdownButton, Tabs, Tab, Nav, ButtonGroup, Pagination, FloatingLabel, Accordion, Collapse } from 'react-bootstrap';
import moment from 'moment';
import UserAvatar from './UserAvatar';
import { FaCheck, FaTimes, FaGraduationCap, FaClipboardList, FaFileAlt, FaArrowUp, FaClock, FaSyringe, FaIdCard, FaChalkboardTeacher } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/api';
import ProgramPGYUtils from '../utils/programPGYUtils';
import {
  VACCINATION_CARD_ITEM_NAME,
  VACCINES,
  mapPromotionVaccineNameToCardVaccineId
} from '../constants/vaccinationCardCatalog';
import VaccinationCardViewModal from './VaccinationCardViewModal';
import PromotionEvidenceReviewTabPanes from './PromotionEvidenceReviewTabPanes';
// Helper to get initial promotion tab from hash
function getInitialPromotionTabFromHash() {
  const hash = window.location.hash.replace('#', '');
  if (hash.startsWith('promotion-')) {
    const tab = hash.split('-')[1];
    if (["residents","requirements","exams"].includes(tab)) {
      return tab;
    }
  }
  return 'residents';
}

const CATEGORY_OPTIONS = {
  licensesCertifications: ['BLS', 'ACLS', 'PALS', 'NRP', 'SCLS', 'ATLS', 'ABLS', 'ACLS-EP', 'EMS', 'Other'],
  onboardingRequirements: ['Onboarding Package', 'Required Document', 'Other'],
  trainingCertifications: ['HIPAA', 'Infection Control', 'Blood Borne Pathogens', 'Fire Safety', 'GME Orientation', 'Other'],
  vaccinations: ['COVID-19', 'Flu', 'MMR', 'Tdap', 'Hepatitis B', 'Varicella', 'Meningococcal', 'HPV', 'Other']
};

const CATEGORY_DESCRIPTIONS = {
  licensesCertifications: 'Track required certifications (BLS/ACLS/ATLS), expiration dates, and uploaded proof documents.',
  onboardingRequirements: 'Select onboarding package/document requirements by PGY level and mark which are required.',
  trainingCertifications: 'Configure required trainings (HIPAA and others), including custom training options.',
  vaccinations: 'Track required vaccinations (COVID/Flu), including date/location and proof upload expectations.',
  scholarlyActivity: 'Require one scholarly submission per line item. Residents choose presentation, conference, or manuscript when they upload evidence. Set whether it is due by end of a PGY year and/or before graduation.'
};

const SCHOLARLY_DEFAULT_NAME = 'Scholarly activity';

function formatScholarlyDueSummary(item) {
  if (!item || (item.dueByEndOfPgy !== true && item.dueBeforeGraduation !== true)) return null;
  const parts = [];
  if (item.dueByEndOfPgy === true) {
    const lvl = Number(item.dueByEndOfPgyLevel);
    parts.push(`End of PGY-${Number.isFinite(lvl) && lvl > 0 ? lvl : 1}`);
  }
  if (item.dueBeforeGraduation === true) parts.push('Before graduation');
  return parts.join(' · ');
}

const EVIDENCE_CATEGORY_KEYS = [
  'licensesCertifications',
  'onboardingRequirements',
  'trainingCertifications',
  'vaccinations',
  'scholarlyActivity'
];

/** Promotion manager sidebar tabs for evidence checklist review */
const CHECKLIST_REVIEW_TAB_KEYS = ['licenses', 'onboarding', 'training', 'vaccinations', 'scholarly'];

function vaccinationEvidenceSortDesc(a, b) {
  const ta = new Date(a?.updatedAt || a?.reviewedAt || 0).getTime();
  const tb = new Date(b?.updatedAt || b?.reviewedAt || 0).getTime();
  return tb - ta;
}

/** Group vaccination + vaccination card evidence by resident; cards first, then individual vaccines. */
function buildVaccinationReviewGroups(rows) {
  const byRes = new Map();
  (rows || []).forEach((item) => {
    const rid = item.residentId ?? item.resident?.id;
    if (rid == null) return;
    if (!byRes.has(rid)) {
      byRes.set(rid, { residentId: rid, resident: item.resident, cardRows: [], vaxRows: [] });
    }
    const g = byRes.get(rid);
    if (item.category === 'vaccinationCard') g.cardRows.push(item);
    else if (item.category === 'vaccinations') g.vaxRows.push(item);
  });
  const groups = Array.from(byRes.values());
  groups.forEach((g) => {
    g.cardRows.sort(vaccinationEvidenceSortDesc);
    g.vaxRows.sort(vaccinationEvidenceSortDesc);
  });
  groups.sort((a, b) => {
    const an = `${a.resident?.lastName || ''} ${a.resident?.firstName || ''}`.trim().toLowerCase();
    const bn = `${b.resident?.lastName || ''} ${b.resident?.firstName || ''}`.trim().toLowerCase();
    return an.localeCompare(bn);
  });
  return groups;
}

/** Flat list: card rows (with optional toggle meta) then optional vax header + nested block per resident (Vaccinations Review tab). */
function buildVaccinationDisplayEntries(rows) {
  return buildVaccinationReviewGroups(rows).flatMap((g) => {
    const entries = [];
    const vaxCount = g.vaxRows.length;
    if (g.cardRows.length > 0) {
      g.cardRows.forEach((item, idx) => {
        entries.push({
          kind: 'row',
          item,
          showVaxSubmissionsToggle: vaxCount > 0 && idx === g.cardRows.length - 1,
          vaxSubmissionsCount: vaxCount,
          vaccinationGroupResidentId: g.residentId
        });
      });
    } else if (vaxCount > 0) {
      entries.push({
        kind: 'vaxHeader',
        key: `vax-hdr-${g.residentId}`,
        residentId: g.residentId,
        resident: g.resident,
        vaxSubmissionsCount: vaxCount
      });
    }
    if (vaxCount > 0) {
      entries.push({
        kind: 'vaxNest',
        key: `vax-nest-${g.residentId}`,
        residentId: g.residentId,
        resident: g.resident,
        items: g.vaxRows,
        hasCardAbove: g.cardRows.length > 0
      });
    }
    return entries;
  });
}

function parseStructuredVaccinationCardDetails(details) {
  if (details == null) return null;
  if (typeof details === 'object' && !Array.isArray(details)) return details;
  if (typeof details === 'string') {
    try {
      return JSON.parse(details);
    } catch {
      return null;
    }
  }
  return null;
}

function structuredCardHasDoseForEvidenceIds(cardDetails, evidenceIds) {
  const parsed = parseStructuredVaccinationCardDetails(cardDetails);
  if (!parsed?.vaccines || !(evidenceIds instanceof Set) || evidenceIds.size === 0) return false;
  return Object.values(parsed.vaccines).some(
    (bucket) =>
      bucket &&
      Array.isArray(bucket.doses) &&
      bucket.doses.some((dose) => {
        const id = Number(dose?.promotionEvidenceId);
        return Number.isFinite(id) && evidenceIds.has(id);
      })
  );
}

function buildInitialCategoryDrafts() {
  return {
    licensesCertifications: { enabled: false, composerOpen: false, selectedOption: 'BLS', otherOption: '', info: '', required: true, recurrenceType: 'until_expiration_until_graduation' },
    onboardingRequirements: { enabled: false, composerOpen: false, selectedOption: 'Onboarding Package', otherOption: '', info: '', required: true, onboardingType: 'package', onboardingId: '', forceRequirement: false, recurrenceType: 'until_complete', requiredPgyLevels: [] },
    trainingCertifications: { enabled: false, composerOpen: false, selectedOption: 'HIPAA', otherOption: '', info: '', required: true, recurrenceType: 'yearly_until_graduation' },
    vaccinations: { enabled: false, composerOpen: false, selectedOption: 'COVID-19', otherOption: '', info: '', required: true, recurrenceType: 'yearly_until_graduation' },
    scholarlyActivity: {
      enabled: false,
      composerOpen: false,
      selectedOption: SCHOLARLY_DEFAULT_NAME,
      scholarlyRequirementName: SCHOLARLY_DEFAULT_NAME,
      otherOption: '',
      info: '',
      required: true,
      recurrenceType: 'until_complete',
      dueByEndOfPgy: true,
      dueByEndOfPgyLevel: 1,
      dueBeforeGraduation: false
    }
  };
}

/** When opening the modal with existing program-wide items, turn category on and keep composer closed until "Add another". */
function categoryDraftsForPrefilledItems(categories) {
  const base = buildInitialCategoryDrafts();
  EVIDENCE_CATEGORY_KEYS.forEach((k) => {
    const list = categories[k];
    if (Array.isArray(list) && list.length > 0) {
      const s0 = list[0];
      base[k] = {
        ...base[k],
        enabled: true,
        composerOpen: false,
        recurrenceType: s0?.recurrenceType || base[k].recurrenceType
      };
      if (k === 'scholarlyActivity' && s0) {
        base[k] = {
          ...base[k],
          scholarlyRequirementName: s0.name || SCHOLARLY_DEFAULT_NAME,
          dueByEndOfPgy: s0.dueByEndOfPgy === true,
          dueByEndOfPgyLevel: Number(s0.dueByEndOfPgyLevel) > 0 ? Number(s0.dueByEndOfPgyLevel) : 1,
          dueBeforeGraduation: s0.dueBeforeGraduation === true
        };
      }
    }
  });
  return base;
}

function categoryDraftsForPrefilledItemsWithVaccinationCard(categories, vaccinationCardPgyLevels) {
  const drafts = categoryDraftsForPrefilledItems(categories);
  if (Array.isArray(vaccinationCardPgyLevels) && vaccinationCardPgyLevels.length > 0) {
    drafts.vaccinations = {
      ...drafts.vaccinations,
      enabled: true,
      composerOpen: false
    };
  }
  return drafts;
}

/** Latest evidence row per resident for a given category + item name (by updatedAt). */
function getLatestEvidencePerResident(evidenceRows, category, itemName) {
  const byResident = new Map();
  evidenceRows
    .filter((e) => e.category === category && e.itemName === itemName)
    .forEach((e) => {
      const rid = e.residentId;
      const cur = byResident.get(rid);
      if (!cur || new Date(e.updatedAt) > new Date(cur.updatedAt)) {
        byResident.set(rid, e);
      }
    });
  return byResident;
}

function getEvidenceCompletionCounts(evidenceRows, category, itemName, eligibleResidentIds = null) {
  const byResident = getLatestEvidencePerResident(evidenceRows, category, itemName);
  const eligibleSet = eligibleResidentIds ? new Set(Array.from(eligibleResidentIds).map(String)) : null;
  let approved = 0;
  let submitted = 0;
  let rejected = 0;
  let withRecord = 0;
  byResident.forEach((e, residentId) => {
    if (eligibleSet && !eligibleSet.has(String(residentId))) return;
    withRecord += 1;
    if (e.status === 'approved') approved += 1;
    else if (e.status === 'submitted') submitted += 1;
    else if (e.status === 'rejected') rejected += 1;
  });
  return { approved, submitted, rejected, withRecord };
}

function cloneProgramWideCategories(sample) {
  if (!sample) {
    return {
      licensesCertifications: [],
      onboardingRequirements: [],
      trainingCertifications: [],
      vaccinations: [],
      scholarlyActivity: []
    };
  }
  return {
    licensesCertifications: JSON.parse(JSON.stringify(sample.licensesCertifications || [])),
    onboardingRequirements: JSON.parse(JSON.stringify(sample.onboardingRequirements || [])),
    trainingCertifications: JSON.parse(JSON.stringify(sample.trainingCertifications || [])),
    vaccinations: JSON.parse(JSON.stringify(sample.vaccinations || [])),
    scholarlyActivity: JSON.parse(JSON.stringify(sample.scholarlyActivity || []))
  };
}

function hasAnyProgramWideCategories(cats) {
  return EVIDENCE_CATEGORY_KEYS.some((k) => Array.isArray(cats[k]) && cats[k].length > 0);
}

function getOnboardingChecklistItemsForYear(requirements, academicYear) {
  const y = parseInt(academicYear, 10);
  const row = (requirements || []).find((r) => parseInt(r.academicYear, 10) === y);
  return (row?.onboardingRequirements || []).filter((it) => it && it.required);
}

function buildOnboardingReviewMergedRows({
  promotionEvidence,
  submissions,
  assignments,
  residents,
  requirements,
  selectedAcademicYear
}) {
  const items = getOnboardingChecklistItemsForYear(requirements, selectedAcademicYear);
  const promoOnb = (promotionEvidence || []).filter((e) => e.category === 'onboardingRequirements');
  if (!items.length) return promoOnb;

  const synth = [];
  const covered = new Set();

  (submissions || []).forEach((sub) => {
    const docId = sub.documentId;
    if (!docId) return;
    const item = items.find(
      (it) => it.onboardingType === 'document' && Number(it.onboardingId) === Number(docId)
    );
    if (!item) return;
    const rid = sub.submittedBy;
    const res = (residents || []).find((r) => r.id === rid);
    if (
      Array.isArray(item.requiredPgyLevels) &&
      item.requiredPgyLevels.length > 0 &&
      res &&
      !item.requiredPgyLevels.map(Number).includes(Number(res.year))
    ) {
      return;
    }
    const key = `${rid}::${item.name}`;
    covered.add(key);
    synth.push({
      id: `onb-sub-${sub.id}`,
      residentId: rid,
      category: 'onboardingRequirements',
      itemName: item.name,
      resident: sub.submitter,
      status: sub.status === 'approved' ? 'approved' : 'submitted',
      evidencePath: sub.webPath,
      details: null,
      isOnboardingBridge: true,
      bridgeKind: 'document_submission',
      onboardingSubmissionId: sub.id
    });
  });

  (assignments || []).forEach((a) => {
    if (!a.packageId) return;
    const item = items.find((it) => {
      if (it.onboardingType !== 'package' || Number(it.onboardingId) !== Number(a.packageId)) return false;
      const nm = (it.name || '').trim();
      if (!nm) return true;
      return (a.customName || '').trim() === nm;
    });
    if (!item) return;
    const rid = a.assignedToId;
    const res = (residents || []).find((r) => r.id === rid);
    if (
      Array.isArray(item.requiredPgyLevels) &&
      item.requiredPgyLevels.length > 0 &&
      res &&
      !item.requiredPgyLevels.map(Number).includes(Number(res.year))
    ) {
      return;
    }
    const key = `${rid}::${item.name}`;
    covered.add(key);
    const pkgStatus = a.status === 'completed' ? 'approved' : 'assigned';
    synth.push({
      id: `onb-pkg-${a.id}`,
      residentId: rid,
      category: 'onboardingRequirements',
      itemName: item.name,
      resident: a.assignee
        ? { ...a.assignee, year: a.assignee.year ?? res?.year }
        : { id: rid, firstName: '', lastName: '' },
      status: pkgStatus === 'approved' ? 'approved' : 'submitted',
      evidencePath: null,
      details: null,
      isOnboardingBridge: true,
      bridgeKind: 'package_assignment',
      onboardingAssignmentId: a.id
    });
  });

  const filteredPromo = promoOnb.filter((e) => !covered.has(`${e.residentId}::${e.itemName}`));
  return [...synth, ...filteredPromo];
}

const ResidentPromotionManager = () => {
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [showGraduateModal, setShowGraduateModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showBacktrackModal, setShowBacktrackModal] = useState(false);
  const [selectedResident, setSelectedResident] = useState(null);
  const [promotionDate, setPromotionDate] = useState(moment().format('YYYY-MM-DD'));
  const [promoting, setPromoting] = useState(false);
  const [backtracking, setBacktracking] = useState(false);
  const [promoteAllLoading, setPromoteAllLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [academicYear, setAcademicYear] = useState(null);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [pgyFilter, setPgyFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [showEligibilityModal, setShowEligibilityModal] = useState(false);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [showRequirementsModal, setShowRequirementsModal] = useState(false);
  const [requirementsModalNav, setRequirementsModalNav] = useState('pgy-specific');
  const [pgySpecificDirty, setPgySpecificDirty] = useState(false);
  const [programWideDirty, setProgramWideDirty] = useState(false);

  // Copy requirements from another academic year
  const [showCopyRequirementsModal, setShowCopyRequirementsModal] = useState(false);
  const [copyRequirementsMode, setCopyRequirementsMode] = useState({
    copyPgySpecific: true,
    copyProgramWideEvidence: true
  });
  const [copyRequirementsSourceAcademicYear, setCopyRequirementsSourceAcademicYear] = useState(new Date().getFullYear() - 1);
  const [copyRequirementsLoading, setCopyRequirementsLoading] = useState(false);
  const [copyRequirementsError, setCopyRequirementsError] = useState('');

  /** Submenu under "Checklist items" for evidence review tabs (desktop + mobile nav) */
  const [checklistNavOpen, setChecklistNavOpen] = useState(false);
  const [requirements, setRequirements] = useState([]);
  const [evaluationTemplates, setEvaluationTemplates] = useState([]);
  const [currentAcademicYear, setCurrentAcademicYear] = useState(null);
  const [showInServiceModal, setShowInServiceModal] = useState(false);
  const [inServiceExams, setInServiceExams] = useState([]);
  const [activeTab, setActiveTab] = useState(getInitialPromotionTabFromHash());
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(new Date().getFullYear());
  const [editingExam, setEditingExam] = useState(null);
  const [isEditingExam, setIsEditingExam] = useState(false);
  const [isEditingRequirement, setIsEditingRequirement] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);

  // Form states
  const [requirementForm, setRequirementForm] = useState({
    pgyLevel: 1,
    academicYear: new Date().getFullYear(),
    requiredCaseLogs: '',
    evaluationTemplateId: '',
    requiresInServiceExam: true,
    inServiceExamPassRequired: true,
    licensesCertifications: [],
    onboardingRequirements: [],
    trainingCertifications: [],
    vaccinations: [],
    scholarlyActivity: [],
    vaccinationCardPgyLevels: []
  });
  const [categoryDrafts, setCategoryDrafts] = useState(buildInitialCategoryDrafts);
  const [promotionEvidence, setPromotionEvidence] = useState([]);
  const [onboardingDirectorSubmissions, setOnboardingDirectorSubmissions] = useState([]);
  const [onboardingDirectorAssignments, setOnboardingDirectorAssignments] = useState([]);
  const [onboardingPackages, setOnboardingPackages] = useState([]);
  const [onboardingDocuments, setOnboardingDocuments] = useState([]);
  const [vaccinationCardViewItem, setVaccinationCardViewItem] = useState(null);
  /** Vaccinations Review: residentId keys whose individual-vaccination submission panel is expanded */
  const [vaccinationSubmissionsExpandedIds, setVaccinationSubmissionsExpandedIds] = useState(() => new Set());

  const toggleVaccinationSubmissionsPanel = (residentId) => {
    const key = String(residentId);
    setVaccinationSubmissionsExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const [inServiceForm, setInServiceForm] = useState({
    residentId: '',
    examDate: '',
    academicYear: new Date().getFullYear(),
    pgyLevel: 1,
    result: 'pass',
    score: '',
    notes: ''
  });

  // Add state for showing the confirm modal and the action to confirm
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(() => () => {});
  const [confirmMessage, setConfirmMessage] = useState('Are you sure?');
  const [showRejectReasonModal, setShowRejectReasonModal] = useState(false);
  const [rejectReasonText, setRejectReasonText] = useState('');
  const [rejectTargetEvidence, setRejectTargetEvidence] = useState(null);
  
  // PGY level configuration
  const [pgyLevelOptions, setPgyLevelOptions] = useState([]);
  const [maxPGYLevel, setMaxPGYLevel] = useState(5);
  const [programDetails, setProgramDetails] = useState(null);
  const [programLoading, setProgramLoading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Fetch program details and PGY levels
  const fetchProgramDetails = async () => {
    setProgramLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/program-details`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        console.log('Program details received:', data);
        setProgramDetails(data);
        
        // Fetch PGY levels for this program
        if (data.id) {
          console.log('Fetching PGY levels for programId:', data.id);
          await fetchPGYLevels(data.id);
        } else {
          console.log('No programId found in program details');
        }
      }
    } catch (err) {
      console.error("Failed to fetch program details", err);
    } finally {
      setProgramLoading(false);
    }
  };

  // Fetch PGY level options for the program
  const fetchPGYLevels = async (programId) => {
    try {
      console.log('Fetching PGY levels for program:', programId);
      const options = await ProgramPGYUtils.getPGYLevelOptions(programId);
      console.log('Received PGY options:', options);
      setPgyLevelOptions(options);
      setMaxPGYLevel(options.length > 0 ? options[options.length - 1].value : 5);
    } catch (error) {
      console.error('Error fetching PGY levels:', error);
      // Fallback to default 5 levels
      const fallbackOptions = Array.from({ length: 5 }, (_, i) => ({
        value: i + 1,
        label: `PGY-${i + 1}`
      }));
      console.log('Using fallback PGY options:', fallbackOptions);
      setPgyLevelOptions(fallbackOptions);
      setMaxPGYLevel(5);
    }
  };

  const checkEligibility = async (residentId) => {
    try {
      console.log('🔍 Checking eligibility for resident:', residentId);
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_ENDPOINTS.PROMOTIONS}/residents/${residentId}/eligibility`, { headers });
      console.log('✅ Eligibility response:', response.data);
      console.log('🔍 Eligibility data structure:', {
        hasGraduationEligible: 'graduationEligible' in response.data.eligibility,
        graduationEligible: response.data.eligibility.graduationEligible,
        requirements: response.data.eligibility.requirements,
        checks: response.data.eligibility.checks,
        eligible: response.data.eligibility.eligible
      });
      // Add the residentId to the eligibility data
      const eligibilityWithResidentId = {
        ...response.data.eligibility,
        residentId: residentId
      };
      setEligibilityData(eligibilityWithResidentId);
      setShowEligibilityModal(true);
    } catch (err) {
      console.error('❌ Error checking eligibility:', err);
      setError(err.response?.data?.message || 'Failed to check eligibility');
    }
  };

  useEffect(() => {
    console.log('🚀 ResidentPromotionManager mounted');
    fetchData();
    fetchProgramDetails(); // Fetch program details and PGY levels
  }, []);

  // Get current year
  const currentYear = new Date().getFullYear();

  const fetchData = async () => {
    try {
      console.log('📡 Starting fetchData...');
      setLoading(true);
      const headers = getAuthHeaders();
      
      console.log('🌐 Making API calls to:');
      console.log('  - /api/promotions/residents');
      console.log('  - /api/promotions/academic-year');
      console.log('  - /api/promotions/evaluation-templates');
      
      const [residentsRes, academicYearRes, templatesRes] = await Promise.all([
        axios.get(`${API_ENDPOINTS.PROMOTIONS}/residents`, { headers }),
        axios.get(`${API_ENDPOINTS.PROMOTIONS}/academic-year`, { headers }),
        axios.get(`${API_ENDPOINTS.PROMOTIONS}/evaluation-templates`, { headers })
      ]);

      console.log('✅ API responses received:');
      console.log('  - Residents response:', residentsRes.data);
      console.log('  - Academic year response:', academicYearRes.data);
      console.log('  - Templates response:', templatesRes.data);

      setResidents(residentsRes.data.residents);

      setCurrentAcademicYear(academicYearRes.data.academicYear);
      setEvaluationTemplates(templatesRes.data.templates);
      
      // Set default academic year in forms
      const defaultYear = academicYearRes.data.academicYear.year;
      setRequirementForm(prev => ({ ...prev, academicYear: defaultYear }));
      setInServiceForm(prev => ({ ...prev, academicYear: defaultYear }));
      setSelectedAcademicYear(defaultYear);
      
      // Fetch requirements and exams for the default year
      await Promise.all([
        fetchRequirements(defaultYear),
        fetchInServiceExams(defaultYear)
      ]);
      
      console.log('✅ Data successfully set in state');
      
    } catch (err) {
      console.error('❌ Error in fetchData:', err);
      console.error('❌ Error response:', err.response);
      console.error('❌ Error status:', err.response?.status);
      console.error('❌ Error data:', err.response?.data);
      console.error('❌ Error config:', err.config);
      
      setError('Failed to fetch data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
      console.log('🏁 fetchData completed');
    }
  };

  const promoteResident = async (residentId) => {
    try {
      console.log('🎓 Promoting resident:', residentId);
      console.log('📅 Promotion date:', promotionDate);
      const headers = getAuthHeaders();
      const response = await axios.post(`${API_ENDPOINTS.PROMOTIONS}/residents/${residentId}/promote`, {
        promotionDate: promotionDate
      }, { headers });
      console.log('✅ Promotion response:', response.data);
      if (response.data.success) {
        fetchData(); // Refresh data
        setError(null);
        alert(`Successfully promoted ${selectedResident.firstName} ${selectedResident.lastName} to PGY-${selectedResident.year + 1}!`);
        setShowPromoteModal(false);
      }
    } catch (err) {
      console.error('❌ Error promoting resident:', err);
      console.error('❌ Error response:', err.response?.data);
      
      // Show detailed requirements message
      const errorMessage = err.response?.data?.message || 'Failed to promote resident';
      if (errorMessage.includes('not eligible for promotion')) {
        const requirements = errorMessage.split(': ')[1] || '';
        const requirementsList = requirements.split(', ');
        
        let message = `❌ ${selectedResident.firstName} ${selectedResident.lastName} is not eligible for promotion.\n\n`;
        message += `📋 Requirements that need to be met:\n`;
        requirementsList.forEach(req => {
          message += `• ${req}\n`;
        });
        message += `\n💡 To meet these requirements:\n`;
        message += `• Add case logs through the case log system\n`;
        message += `• Record in-service exam results in the "In-Service Exams" tab\n`;
        message += `• Complete required evaluations\n`;
        
        alert(message);
      } else {
        setError(errorMessage);
      }
    }
  };

  const graduateResident = async () => {
    if (!selectedResident || !selectedResident.id) {
      console.error('❌ No resident selected or resident ID is missing');
      setError('No resident selected for graduation');
      return;
    }

    try {
      console.log('🎓 Graduating resident:', selectedResident.id);
      console.log('📅 Graduation date:', promotionDate);
      console.log('👤 Selected resident:', selectedResident);
      
      const headers = getAuthHeaders();
      const response = await axios.post(`${API_ENDPOINTS.GRADUATIONS}/residents/${selectedResident.id}/graduate`, {
        graduationDate: promotionDate,
        notes: 'Graduated through promotion manager'
      }, { headers });
      console.log('✅ Graduation response:', response.data);
      if (response.data.success) {
        fetchData(); // Refresh data
        setError(null);
        alert(`Successfully graduated ${selectedResident.firstName} ${selectedResident.lastName}!`);
        setShowGraduateModal(false);
      }
    } catch (err) {
      console.error('❌ Error graduating resident:', err);
      console.error('❌ Error response:', err.response?.data);
      
      const errorMessage = err.response?.data?.message || 'Failed to graduate resident';
      setError(errorMessage);
    }
  };

  const promoteAllResidents = async () => {
    if (!window.confirm('Are you sure you want to promote all eligible residents?')) {
      return;
    }

    try {
      console.log('🎓 Promoting all eligible residents');
      const headers = getAuthHeaders();
      const response = await axios.post(`${API_ENDPOINTS.PROMOTIONS}/promote-all`, {}, { headers });
      console.log('✅ Promote all response:', response.data);
      if (response.data.success) {
        fetchData(); // Refresh data
        setError(null);
        alert(`Promoted ${response.data.totalPromoted} residents. ${response.data.totalIneligible} were ineligible.`);
      }
    } catch (err) {
      console.error('❌ Error promoting all residents:', err);
      console.error('❌ Error response:', err.response?.data);
      setError(err.response?.data?.message || 'Failed to promote residents');
    }
  };

  const fetchRequirements = async (academicYear) => {
    try {
      console.log('📋 Fetching requirements for academic year:', academicYear);
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_ENDPOINTS.PROMOTIONS}/requirements/academic-year/${academicYear}`, { headers });
      console.log('✅ Requirements response:', response.data);
      setRequirements(response.data.requirements);
    } catch (err) {
      console.error('❌ Error fetching requirements:', err);
      console.error('❌ Error response:', err.response?.data);
      setError('Failed to fetch requirements');
    }
  };

  const saveRequirement = async (e) => {
    e.preventDefault();
    try {
      console.log('💾 Saving requirement:', requirementForm);
      const headers = getAuthHeaders();
      const nothingDirty = !pgySpecificDirty && !programWideDirty;
      const savePgySpecific = pgySpecificDirty || nothingDirty;
      const saveProgramWideEvidence = programWideDirty || nothingDirty;

      await axios.post(
        `${API_ENDPOINTS.PROMOTIONS}/requirements`,
        { ...requirementForm, savePgySpecific, saveProgramWideEvidence },
        { headers }
      );
      console.log('✅ Requirement saved successfully');
      setShowRequirementsModal(false);
      fetchRequirements(requirementForm.academicYear);
      fetchOnboardingDirectorReviewData();
      setPgySpecificDirty(false);
      setProgramWideDirty(false);
      
      // Only reset form if we're creating a new requirement, not editing
      if (!isEditingRequirement) {
        resetRequirementForm();
      }
      setIsEditingRequirement(false);
    } catch (err) {
      console.error('❌ Error saving requirement:', err);
      console.error('❌ Error response:', err.response?.data);
      setError('Failed to save requirement');
    }
  };

  const fetchInServiceExams = async (academicYear) => {
    try {
      console.log('📝 Fetching in-service exams for academic year:', academicYear);
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_ENDPOINTS.PROMOTIONS}/inservice-exams/academic-year/${academicYear}`, { headers });
      console.log('✅ In-service exams response:', response.data);
      setInServiceExams(response.data.results);
    } catch (err) {
      console.error('❌ Error fetching in-service exams:', err);
      console.error('❌ Error response:', err.response?.data);
      setError('Failed to fetch in-service exams');
    }
  };

  const saveInServiceExam = async (e) => {
    e.preventDefault();
    try {
      console.log('💾 Saving in-service exam:', inServiceForm);
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      };
      const formData = new FormData();
      Object.entries(inServiceForm).forEach(([key, value]) => {
        formData.append(key, value);
      });
      if (pdfFile) {
        formData.append('pdfFile', pdfFile);
      }
      if (isEditingExam && editingExam) {
        await axios.put(`${API_ENDPOINTS.PROMOTIONS}/inservice-exams/${editingExam.id}`, formData, { headers });
        console.log('✅ In-service exam updated successfully');
      } else {
        await axios.post(`${API_ENDPOINTS.PROMOTIONS}/inservice-exams`, formData, { headers });
        console.log('✅ In-service exam saved successfully');
      }
      setShowInServiceModal(false);
      fetchInServiceExams(inServiceForm.academicYear);
      resetInServiceForm();
      setPdfFile(null);
    } catch (err) {
      console.error('❌ Error saving in-service exam:', err);
      console.error('❌ Error response:', err.response?.data);
      setError('Failed to save in-service exam');
    }
  };

  const editInServiceExam = (exam) => {
    setEditingExam(exam);
    setIsEditingExam(true);
    setInServiceForm({
      residentId: exam.residentId,
      examDate: exam.examDate,
      academicYear: exam.academicYear,
      pgyLevel: exam.pgyLevel,
      result: exam.result,
      score: exam.score || '',
      notes: exam.notes || ''
    });
    setShowInServiceModal(true);
  };

  const deleteInServiceExam = async (examId) => {
    if (!window.confirm('Are you sure you want to delete this in-service exam record? This action cannot be undone.')) {
      return;
    }

    try {
      console.log('🗑️ Deleting in-service exam:', examId);
      const headers = getAuthHeaders();
      await axios.delete(`${API_ENDPOINTS.PROMOTIONS}/inservice-exams/${examId}`, { headers });
      console.log('✅ In-service exam deleted successfully');
      fetchInServiceExams(selectedAcademicYear);
    } catch (err) {
      console.error('❌ Error deleting in-service exam:', err);
      console.error('❌ Error response:', err.response?.data);
      setError('Failed to delete in-service exam');
    }
  };

  const deleteRequirement = async (pgyLevel, academicYear) => {
    if (!window.confirm(`Are you sure you want to delete the promotion requirements for PGY-${pgyLevel} in academic year ${academicYear}-${academicYear + 1}? This action cannot be undone.`)) {
      return;
    }

    try {
      console.log('🗑️ Deleting promotion requirements:', { pgyLevel, academicYear });
      const headers = getAuthHeaders();
      await axios.delete(`${API_ENDPOINTS.PROMOTIONS}/requirements/${pgyLevel}/${academicYear}`, { headers });
      console.log('✅ Promotion requirements deleted successfully');
      fetchRequirements(selectedAcademicYear);
    } catch (err) {
      console.error('❌ Error deleting promotion requirements:', err);
      console.error('❌ Error response:', err.response?.data);
      setError('Failed to delete promotion requirements');
    }
  };

  const resetInServiceForm = () => {
    setInServiceForm({
      residentId: '',
      examDate: '',
      academicYear: currentAcademicYear?.year || new Date().getFullYear(),
      pgyLevel: 1,
      result: 'pass',
      score: '',
      notes: ''
    });
    setEditingExam(null);
    setIsEditingExam(false);
  };

  const resetRequirementForm = () => {
    setCategoryDrafts(buildInitialCategoryDrafts());
    setRequirementForm({
      pgyLevel: 1,
      academicYear: selectedAcademicYear,
      requiredCaseLogs: '',
      evaluationTemplateId: '',
      requiresInServiceExam: true,
      inServiceExamPassRequired: true,
      licensesCertifications: [],
      onboardingRequirements: [],
      trainingCertifications: [],
      vaccinations: [],
      scholarlyActivity: [],
      vaccinationCardPgyLevels: []
    });
    setIsEditingRequirement(false);
  };

  const openCopyRequirementsModal = ({ copyPgySpecific = true, copyProgramWideEvidence = true } = {}) => {
    const defaultSource = parseInt(selectedAcademicYear, 10) - 1;
    setCopyRequirementsMode({ copyPgySpecific: !!copyPgySpecific, copyProgramWideEvidence: !!copyProgramWideEvidence });
    setCopyRequirementsSourceAcademicYear(Number.isFinite(defaultSource) ? defaultSource : new Date().getFullYear() - 1);
    setCopyRequirementsError('');
    setShowCopyRequirementsModal(true);
  };

  const handleCopyRequirements = async (e) => {
    e?.preventDefault?.();
    setCopyRequirementsLoading(true);
    setCopyRequirementsError('');

    try {
      const headers = getAuthHeaders();
      await axios.post(
        `${API_ENDPOINTS.PROMOTIONS}/requirements/copy`,
        {
          sourceAcademicYear: copyRequirementsSourceAcademicYear,
          targetAcademicYear: selectedAcademicYear,
          copyPgySpecific: copyRequirementsMode.copyPgySpecific,
          copyProgramWideEvidence: copyRequirementsMode.copyProgramWideEvidence
        },
        { headers }
      );

      setShowCopyRequirementsModal(false);
      fetchRequirements(selectedAcademicYear);
    } catch (err) {
      setCopyRequirementsError(err.response?.data?.message || 'Failed to copy requirements');
      console.error('❌ Error copying requirements:', err);
    } finally {
      setCopyRequirementsLoading(false);
    }
  };

  const openPgySpecificRequirementsModal = () => {
    setRequirementsModalNav('pgy-specific');
    setShowRequirementsModal(true);
  };

  /** Add Requirements: pre-fill program-wide evidence lists from any existing row for this academic year. */
  const openAddRequirementsModal = () => {
    const year = selectedAcademicYear;
    const sample = requirements.find((r) => parseInt(r.academicYear, 10) === parseInt(year, 10));
    const cats = cloneProgramWideCategories(sample);
    const pgyLevel = sample?.pgyLevel ? sample.pgyLevel : 1;
    const vCardLevels = (requirements || [])
      .filter((r) => parseInt(r.academicYear, 10) === parseInt(year, 10) && r.vaccinationCardRequired)
      .map((r) => r.pgyLevel)
      .sort((a, b) => a - b);
    setRequirementForm({
      pgyLevel,
      academicYear: year,
      requiredCaseLogs: sample?.requiredCaseLogs ?? '',
      evaluationTemplateId: sample?.evaluationTemplateId ?? '',
      requiresInServiceExam: sample?.requiresInServiceExam ?? true,
      inServiceExamPassRequired: sample?.inServiceExamPassRequired ?? true,
      ...cats,
      vaccinationCardPgyLevels: vCardLevels
    });
    setCategoryDrafts(categoryDraftsForPrefilledItemsWithVaccinationCard(cats, vCardLevels));
    setIsEditingRequirement(false);
    setRequirementsModalNav('program-wide');
    setShowRequirementsModal(true);
  };

  const getEligibleResidentIdsForEvidence = (field, item = null) => {
    if (field === 'vaccinationCard') {
      const levels = Array.isArray(requirementForm.vaccinationCardPgyLevels)
        ? requirementForm.vaccinationCardPgyLevels.map((n) => parseInt(n, 10)).filter((n) => Number.isFinite(n))
        : [];
      if (!levels.length) return null;
      return new Set(
        residents
          .filter((res) => levels.includes(parseInt(res?.year, 10)))
          .map((res) => String(res.id))
      );
    }

    if (field === 'onboardingRequirements' && item && Array.isArray(item.requiredPgyLevels) && item.requiredPgyLevels.length > 0) {
      const levels = item.requiredPgyLevels.map((n) => parseInt(n, 10)).filter((n) => Number.isFinite(n));
      return new Set(
        residents
          .filter((res) => levels.includes(parseInt(res?.year, 10)))
          .map((res) => String(res.id))
      );
    }

    return null;
  };

  const renderEvidenceCompletionCell = (field, itemName, item = null) => {
    const eligibleResidentIds = getEligibleResidentIdsForEvidence(field, item);
    const total = eligibleResidentIds ? eligibleResidentIds.size : residents.length;
    if (!total) {
      return <span className="text-muted small">—</span>;
    }
    const { approved, submitted, rejected, withRecord } = getEvidenceCompletionCounts(
      promotionEvidence,
      field,
      itemName,
      eligibleResidentIds
    );
    return (
      <div className="small">
        <span className="text-dark">{approved}/{total} approved</span>
        {submitted > 0 ? <div className="text-muted">{submitted} awaiting review</div> : null}
        {rejected > 0 ? <div className="text-danger">{rejected} rejected (latest)</div> : null}
        {withRecord === 0 ? <div className="text-muted">No submissions yet</div> : null}
      </div>
    );
  };

  const addCategoryItem = (field) => {
    const draft = categoryDrafts[field];
    if (!draft.enabled || !draft.composerOpen) return;
    if (field === 'onboardingRequirements' && !draft.onboardingId) return;
    let selectedName =
      field === 'scholarlyActivity'
        ? String(draft.scholarlyRequirementName || SCHOLARLY_DEFAULT_NAME).trim() || SCHOLARLY_DEFAULT_NAME
        : draft.selectedOption === 'Other'
          ? draft.otherOption.trim()
          : draft.selectedOption;
    if (field === 'onboardingRequirements') {
      const selectedRecord = draft.onboardingType === 'package'
        ? onboardingPackages.find((pkg) => String(pkg.id) === String(draft.onboardingId))
        : onboardingDocuments.find((doc) => String(doc.id) === String(draft.onboardingId));
      selectedName = selectedRecord ? (selectedRecord.name || selectedRecord.title) : '';
    }
    if (!selectedName) return;
    setProgramWideDirty(true);
    const item = {
      name: selectedName,
      info: draft.info.trim(),
      required: !!draft.required,
      recurrenceType: field === 'scholarlyActivity' ? 'until_complete' : draft.recurrenceType
    };
    if (field === 'scholarlyActivity') {
      let dueByEndOfPgy = !!draft.dueByEndOfPgy;
      let dueBeforeGraduation = !!draft.dueBeforeGraduation;
      if (!dueByEndOfPgy && !dueBeforeGraduation) dueBeforeGraduation = true;
      const cap = Math.max(1, maxPGYLevel || 5);
      item.dueByEndOfPgy = dueByEndOfPgy;
      item.dueBeforeGraduation = dueBeforeGraduation;
      item.dueByEndOfPgyLevel = dueByEndOfPgy
        ? Math.min(cap, Math.max(1, parseInt(draft.dueByEndOfPgyLevel, 10) || 1))
        : null;
    }
    if (field === 'onboardingRequirements') {
      item.onboardingType = draft.onboardingType;
      item.onboardingId = draft.onboardingId ? parseInt(draft.onboardingId, 10) : null;
      item.forceRequirement = !!draft.forceRequirement;
      const lv = Array.isArray(draft.requiredPgyLevels) ? draft.requiredPgyLevels.filter((n) => Number.isFinite(Number(n))).map(Number) : [];
      item.requiredPgyLevels = lv.length ? lv : [];
    }
    setRequirementForm((prev) => ({
      ...prev,
      [field]: [...(prev[field] || []), item]
    }));
    setCategoryDrafts((prev) => ({
      ...prev,
      [field]: field === 'onboardingRequirements'
        ? {
            ...prev[field],
            enabled: true,
            composerOpen: false,
            otherOption: '',
            info: '',
            required: true,
            onboardingType: 'package',
            onboardingId: '',
            forceRequirement: false,
            requiredPgyLevels: []
          }
        : field === 'scholarlyActivity'
          ? {
              ...prev[field],
              enabled: true,
              composerOpen: false,
              otherOption: '',
              info: '',
              required: true,
              recurrenceType: 'until_complete',
              scholarlyRequirementName: SCHOLARLY_DEFAULT_NAME,
              dueByEndOfPgy: true,
              dueByEndOfPgyLevel: 1,
              dueBeforeGraduation: false
            }
          : { ...prev[field], enabled: true, composerOpen: false, otherOption: '', info: '', required: true }
    }));
  };

  const removeCategoryItem = (field, idx) => {
    setProgramWideDirty(true);
    setRequirementForm((prev) => {
      const nextList = (prev[field] || []).filter((_, itemIdx) => itemIdx !== idx);
      if (nextList.length === 0) {
        setCategoryDrafts((d) => ({
          ...d,
          [field]: { ...d[field], composerOpen: true }
        }));
      }
      return { ...prev, [field]: nextList };
    });
  };

  const fetchPromotionEvidence = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_ENDPOINTS.PROMOTIONS}/requirements-evidence`, { headers });
      setPromotionEvidence(response.data.evidence || []);
    } catch (err) {
      setError('Failed to fetch promotion evidence');
    }
  };

  const fetchOnboardingDirectorReviewData = async () => {
    try {
      const headers = getAuthHeaders();
      const [subsRes, assignRes] = await Promise.all([
        axios.get(`${API_ENDPOINTS.ONBOARDING}/submissions`, { headers }),
        axios.get(`${API_ENDPOINTS.ONBOARDING}/assignments`, { headers })
      ]);
      setOnboardingDirectorSubmissions(subsRes.data || []);
      setOnboardingDirectorAssignments(assignRes.data || []);
    } catch (_) {
      setOnboardingDirectorSubmissions([]);
      setOnboardingDirectorAssignments([]);
    }
  };

  const reviewOnboardingDocumentSubmission = async (submissionId, status) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(
        `${API_ENDPOINTS.ONBOARDING}/submissions/${submissionId}/review`,
        { status, reviewNotes: '' },
        { headers }
      );
      await fetchOnboardingDirectorReviewData();
      fetchPromotionEvidence();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update onboarding submission');
    }
  };

  const fetchOnboardingSources = async () => {
    try {
      const headers = getAuthHeaders();
      const [packagesRes, documentsRes] = await Promise.all([
        axios.get(`${API_ENDPOINTS.ONBOARDING}/packages`, { headers }),
        axios.get(`${API_ENDPOINTS.ONBOARDING}/documents`, { headers })
      ]);
      setOnboardingPackages(packagesRes.data || []);
      setOnboardingDocuments(documentsRes.data || []);
    } catch (err) {
      setError('Failed to fetch onboarding packages/documents');
    }
  };

  const reviewEvidence = async (evidenceId, status, reviewNotes = '') => {
    try {
      const headers = getAuthHeaders();
      await axios.post(
        `${API_ENDPOINTS.PROMOTIONS}/requirements-evidence/${evidenceId}/review`,
        { status, reviewNotes: reviewNotes || '' },
        { headers }
      );
      fetchPromotionEvidence();
    } catch (err) {
      setError('Failed to review evidence');
    }
  };

  const updateVaccinationCardEvidenceDetails = async (evidenceId, detailsPayload) => {
    try {
      if (!evidenceId) return false;
      const headers = getAuthHeaders();
      await axios.put(
        `${API_ENDPOINTS.PROMOTIONS}/requirements-evidence/${evidenceId}/vaccination-card`,
        { details: detailsPayload },
        { headers }
      );
      await fetchPromotionEvidence();
      setVaccinationCardViewItem((prev) =>
        prev && String(prev.id) === String(evidenceId)
          ? { ...prev, details: detailsPayload }
          : prev
      );
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update vaccination card details');
      return false;
    }
  };

  const requestRejectEvidence = (item) => {
    setRejectTargetEvidence(item || null);
    setRejectReasonText('');
    setShowRejectReasonModal(true);
  };

  const submitRejectEvidenceWithReason = async () => {
    const note = String(rejectReasonText || '').trim();
    if (!note) {
      setError('Please enter a reason for rejection.');
      return;
    }
    if (!rejectTargetEvidence?.id) {
      setError('Missing evidence item to reject.');
      return;
    }
    await reviewEvidence(rejectTargetEvidence.id, 'rejected', note);
    setShowRejectReasonModal(false);
    setRejectReasonText('');
    setRejectTargetEvidence(null);
  };

  const deletePromotionEvidence = async (evidenceId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API_ENDPOINTS.PROMOTIONS}/requirements-evidence/${evidenceId}`, { headers });
      fetchPromotionEvidence();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete evidence');
    }
  };

  const openDeleteEvidenceConfirm = (item) => {
    const name = item.resident ? `${item.resident.firstName} ${item.resident.lastName}` : 'Resident';
    setConfirmMessage(
      `Permanently remove this submission for ${name} (${item.itemName})? The record will be deleted from the database and any uploaded file will be removed. This cannot be undone.`
    );
    setConfirmAction(() => () => deletePromotionEvidence(item.id));
    setShowConfirmModal(true);
  };

  const normalizeEvidenceDetailsObject = (details) => {
    if (details == null) return {};
    try {
      if (typeof details === 'string') return JSON.parse(details);
      return typeof details === 'object' ? details : {};
    } catch {
      return typeof details === 'string' ? { info: details } : {};
    }
  };

  const formatDetailValueMaybeDate = (value) => {
    const s = value == null ? '' : String(value).trim();
    if (!s) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
    }
    return s;
  };

  const humanizeExtraDetailKey = (key) =>
    key
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const renderEvidenceExpirationCell = (categoryKey, item) => {
    if (categoryKey !== 'licensesCertifications') return '—';
    if (!item.expirationDate) return '—';
    try {
      return new Date(item.expirationDate).toLocaleDateString();
    } catch {
      return String(item.expirationDate);
    }
  };

  const renderVaccinationCardDetailsSummary = (item) => {
    const norm = normalizeEvidenceDetailsObject(item.details);
    const vaccines = norm.vaccines || {};
    const tb = norm.tbScreening || {};
    const vaccineEvidenceCount = Array.isArray(norm.vaccineEvidenceFiles)
      ? norm.vaccineEvidenceFiles.length
      : VACCINES.reduce((sum, v) => (vaccines[v.id]?.cardEvidencePath ? sum + 1 : sum), 0);
    return (
      <div className="small">
        {VACCINES.map((v) => {
          const n = (vaccines[v.id]?.doses || []).length;
          return (
            <div key={v.id}>
              <span className="text-muted">{v.name}:</span> {n} {n === 1 ? 'dose' : 'doses'}
            </div>
          );
        })}
        <div className="mt-1">
          <span className="text-muted">TB diagnosed:</span>{' '}
          {tb.diagnosedTb === 'yes' ? 'Yes' : tb.diagnosedTb === 'no' ? 'No' : '—'}
        </div>
        <div>
          <span className="text-muted">TB x-ray evidence:</span>{' '}
          {tb.chestXrayEvidenceOriginalFileName || (tb.chestXrayEvidencePath ? 'Uploaded' : 'None')}
        </div>
        <div>
          <span className="text-muted">Vaccine card evidence uploads:</span> {vaccineEvidenceCount}
        </div>
      </div>
    );
  };

  const renderEvidenceSubmittedDetailsCell = (categoryKey, item) => {
    if (item.category === 'vaccinationCard') {
      return renderVaccinationCardDetailsSummary(item);
    }
    const norm = normalizeEvidenceDetailsObject(item.details);
    const rows = [];
    const used = new Set();

    const pushRow = (label, rawVal) => {
      const val = rawVal == null ? '' : String(rawVal).trim();
      if (!val) return;
      rows.push({ label, value: formatDetailValueMaybeDate(val) });
    };

    if (categoryKey === 'vaccinations') {
      pushRow('Administration date', norm.administrationDate);
      pushRow('Location', norm.administrationLocation);
      used.add('administrationDate');
      used.add('administrationLocation');
    } else if (categoryKey === 'trainingCertifications') {
      pushRow('Completion date', norm.completionDate);
      used.add('completionDate');
    } else if (categoryKey === 'scholarlyActivity') {
      const st = norm.scholarlyActivityType;
      const typeLbl =
        st === 'presentation'
          ? 'Presentation'
          : st === 'conference'
            ? 'Conference'
            : st === 'manuscript'
              ? 'Submitted abstract or manuscript'
              : st;
      pushRow('Activity type', typeLbl);
      if (st) used.add('scholarlyActivityType');
      pushRow('Venue / publication', norm.venueOrPublication);
      used.add('venueOrPublication');
    }

    Object.keys(norm).forEach((k) => {
      if (k === 'info' || used.has(k)) return;
      const v = norm[k];
      if (v != null && String(v).trim() !== '') {
        rows.push({ label: humanizeExtraDetailKey(k), value: formatDetailValueMaybeDate(v) });
        used.add(k);
      }
    });

    pushRow('Notes', norm.info);
    used.add('info');

    if (rows.length === 0) return <span className="text-muted">—</span>;
    return (
      <div className="small">
        {rows.map((r, i) => (
          <div key={`${r.label}-${i}`}><span className="text-muted">{r.label}:</span> {r.value}</div>
        ))}
      </div>
    );
  };

  const openProtectedEvidenceFile = async (evidencePath) => {
    try {
      if (!evidencePath) return;
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.PROFILE_PICS}${evidencePath}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (err) {
      console.error('Failed to open evidence file:', err);
      setError('Failed to open uploaded evidence file');
    }
  };

  const getEligibilityStatus = (resident) => {
    // This would need to be implemented based on the actual eligibility check
    return 'unknown';
  };

  const renderEligibilityModal = () => (
    <Modal show={showEligibilityModal} onHide={() => setShowEligibilityModal(false)} size="lg" centered>
      <Modal.Header closeButton className="bg-light border-0">
        <Modal.Title>
          <span style={{ fontWeight: 700, fontSize: '1.3rem' }}>
            <FaClipboardList className="me-2 text-primary" /> 
            {eligibilityData?.graduationEligible !== undefined ? 'Graduation Eligibility' : 'Promotion Eligibility'}
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {eligibilityData && (
          <>
            {/* Status Summary Bar */}
            <Alert
              variant={eligibilityData.eligible ? 'success' : 'warning'}
              className="d-flex align-items-center mb-4 shadow-sm"
              style={{ fontSize: '1.1rem', fontWeight: 500 }}
            >
              {eligibilityData.eligible ? (
                <><FaCheck className="me-2 text-success" size={22} /> 
                {eligibilityData.graduationEligible !== undefined ? 'Eligible for Graduation' : 'Eligible for Promotion'}
                </>
              ) : (
                <><FaTimes className="me-2 text-warning" size={22} /> 
                {eligibilityData.graduationEligible !== undefined ? 'Not Eligible for Graduation' : 'Not Eligible for Promotion'}
                </>
              )}
            </Alert>

            {/* Requirements Overview */}
            {eligibilityData.graduationEligible !== undefined ? (
              // Graduation requirements display
              <Row className="mb-4 g-3">
                {eligibilityData.requirements && eligibilityData.requirements.map((requirement, index) => {
                  const check = eligibilityData.checks[requirement.id];
                  const isMet = check?.met || false;
                  
                  // Get appropriate icon based on requirement type
                  const getIcon = (type) => {
                    switch (type) {
                      case 'case_logs': return <FaFileAlt className={`me-2 ${isMet ? 'text-success' : 'text-warning'}`} size={20} />;
                      case 'evaluations': return <FaClipboardList className={`me-2 ${isMet ? 'text-success' : 'text-warning'}`} size={20} />;
                      case 'in_service_exam': return <FaGraduationCap className={`me-2 ${isMet ? 'text-success' : 'text-warning'}`} size={20} />;
                      case 'work_hours': return <FaClock className={`me-2 ${isMet ? 'text-success' : 'text-warning'}`} size={20} />;
                      case 'documentation': return <FaFileAlt className={`me-2 ${isMet ? 'text-success' : 'text-warning'}`} size={20} />;
                      default: return <FaCheck className={`me-2 ${isMet ? 'text-success' : 'text-warning'}`} size={20} />;
                    }
                  };

                  return (
                    <Col md={4} key={requirement.id}>
                      <Card className={`h-100 shadow-sm border-${isMet ? 'success' : 'warning'}`}> 
                        <Card.Body>
                          <div className="d-flex align-items-center mb-2">
                            {getIcon(requirement.requirementType)}
                            <span style={{ fontWeight: 600 }}>{requirement.requirementName}</span>
                          </div>
                          {check ? (
                            <>
                              <div className="mb-1">
                                <strong>Status:</strong> {isMet ? <span className="text-success">Met</span> : <span className="text-warning">Not Met</span>}
                              </div>
                              {check.details && check.details.required && check.details.completed !== undefined && (
                                <div className="mb-1">
                                  <strong>Progress:</strong> {check.details.completed}/{check.details.required}
                                </div>
                              )}
                              {!isMet && check.details && check.details.remaining && (
                                <div className="text-warning small">
                                  <strong>Remaining:</strong> {check.details.remaining} needed
                                </div>
                              )}
                              {check.reason && (
                                <div className="text-muted small mt-2">
                                  {check.reason}
                                </div>
                              )}
                            </>
                          ) : <span className="text-muted">No data</span>}
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            ) : (
              // Promotion requirements display (existing code)
              <Row className="mb-4 g-3">
                <Col md={4}>
                  <Card className={`h-100 shadow-sm border-${eligibilityData.checks?.caseLogs?.met ? 'success' : 'warning'}`}> 
                    <Card.Body>
                      <div className="d-flex align-items-center mb-2">
                        <FaFileAlt className={`me-2 ${eligibilityData.checks?.caseLogs?.met ? 'text-success' : 'text-warning'}`} size={20} />
                        <span style={{ fontWeight: 600 }}>Case Logs</span>
                      </div>
                      {eligibilityData.checks?.caseLogs?.details ? (
                        <>
                          <div className="mb-1">
                            <strong>Status:</strong> {eligibilityData.checks.caseLogs.met ? <span className="text-success">Met</span> : <span className="text-warning">Not Met</span>}
                          </div>
                          <div className="mb-1">
                            <strong>Progress:</strong> {eligibilityData.checks.caseLogs.details.completed}/{eligibilityData.checks.caseLogs.details.required}
                          </div>
                          {!eligibilityData.checks.caseLogs.met && (
                            <div className="text-warning small">
                              <strong>Remaining:</strong> {eligibilityData.checks.caseLogs.details.remaining} needed
                            </div>
                          )}
                        </>
                      ) : <span className="text-muted">No data</span>}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className={`h-100 shadow-sm border-${eligibilityData.checks?.inServiceExam?.met ? 'success' : 'warning'}`}> 
                    <Card.Body>
                      <div className="d-flex align-items-center mb-2">
                        <FaGraduationCap className={`me-2 ${eligibilityData.checks?.inServiceExam?.met ? 'text-success' : 'text-warning'}`} size={20} />
                        <span style={{ fontWeight: 600 }}>In-Service Exam</span>
                      </div>
                      {eligibilityData.checks?.inServiceExam?.details ? (
                        <>
                          <div className="mb-1">
                            <strong>Status:</strong> {eligibilityData.checks.inServiceExam.met ? <span className="text-success">Met</span> : <span className="text-warning">Not Met</span>}
                          </div>
                          <div className="mb-1">
                            <strong>Required:</strong> {eligibilityData.checks.inServiceExam.details.required ? 'Yes' : 'No'}
                          </div>
                          {eligibilityData.checks.inServiceExam.details.result && (
                            <div className="mb-1">
                              <strong>Result:</strong> {eligibilityData.checks.inServiceExam.details.result}
                            </div>
                          )}
                          {eligibilityData.checks.inServiceExam.details.examDate && (
                            <div className="mb-0">
                              <strong>Exam Date:</strong> {new Date(eligibilityData.checks.inServiceExam.details.examDate).toLocaleDateString()}
                            </div>
                          )}
                        </>
                      ) : <span className="text-muted">No data</span>}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className={`h-100 shadow-sm border-${eligibilityData.checks?.evaluations?.met ? 'success' : 'warning'}`}> 
                    <Card.Body>
                      <div className="d-flex align-items-center mb-2">
                        <FaClipboardList className={`me-2 ${eligibilityData.checks?.evaluations?.met ? 'text-success' : 'text-warning'}`} size={20} />
                        <span style={{ fontWeight: 600 }}>Evaluations</span>
                      </div>
                      {eligibilityData.checks?.evaluations?.details ? (
                        <>
                          <div className="mb-1">
                            <strong>Status:</strong> {eligibilityData.checks.evaluations.met ? <span className="text-success">Met</span> : <span className="text-warning">Not Met</span>}
                          </div>
                          <div className="mb-1">
                            <strong>Progress:</strong> {eligibilityData.checks.evaluations.details.completed}/{eligibilityData.checks.evaluations.details.required}
                          </div>
                          {eligibilityData.checks.evaluations.details.checks && (
                            <div className="small">
                              <strong>Details:</strong>
                              <ul className="mb-0 mt-1">
                                {eligibilityData.checks.evaluations.details.checks.map((check, index) => (
                                  <li key={index}>
                                    {check.type} ({check.dueDate}): {check.completed ? <span className="text-success">Completed</span> : <span className="text-warning">Not Completed</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      ) : <span className="text-muted">No data</span>}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Requirements Details Card */}
            {eligibilityData.requirements && (
              <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-white">
                  <span className="fw-bold">
                    {eligibilityData.graduationEligible !== undefined ? 'Graduation Requirements' : 'Promotion Requirements'}
                  </span>
                </Card.Header>
                <Card.Body>
                  {eligibilityData.graduationEligible !== undefined ? (
                    // Graduation requirements display
                    <div>
                      <div className="mb-3">
                        <strong>Academic Year:</strong> {eligibilityData.requirements[0]?.academicYear}-{eligibilityData.requirements[0]?.academicYear + 1}
                      </div>
                      <div className="mb-3">
                        <strong>Total Requirements:</strong> {eligibilityData.requirements.length}
                      </div>
                      {eligibilityData.checks && Object.keys(eligibilityData.checks).length > 0 && (
                        <div>
                          <strong>Requirement Status:</strong>
                          <ul className="mt-2">
                            {Object.entries(eligibilityData.checks).map(([reqId, check]) => {
                              const requirement = eligibilityData.requirements.find(r => r.id == reqId);
                              return (
                                <li key={reqId} className={check.met ? 'text-success' : 'text-warning'}>
                                  <strong>{requirement?.requirementName || 'Unknown Requirement'}:</strong> {check.reason}
                                  {check.details && (
                                    <div className="ms-3 small">
                                      {check.details.required && check.details.completed !== undefined && (
                                        <span>Progress: {check.details.completed}/{check.details.required}</span>
                                      )}
                                      {check.details.status && <span>Status: {check.details.status}</span>}
                                    </div>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Promotion requirements display (existing code)
                    <Row>
                      <Col md={6}>
                        <div><strong>PGY Level:</strong> {eligibilityData.requirements.pgyLevel}</div>
                        <div><strong>Academic Year:</strong> {eligibilityData.requirements.academicYear}-{eligibilityData.requirements.academicYear + 1}</div>
                        <div><strong>Required Case Logs:</strong> {eligibilityData.requirements.requiredCaseLogs}</div>
                      </Col>
                      <Col md={6}>
                        <div><strong>In-Service Exam Required:</strong> {eligibilityData.requirements.requiresInServiceExam ? 'Yes' : 'No'}</div>
                        <div><strong>Pass Required:</strong> {eligibilityData.requirements.inServiceExamPassRequired ? 'Yes' : 'No'}</div>
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Reasons and How to Meet Requirements */}
            {!eligibilityData.eligible && eligibilityData.reasons && (
              <Alert variant="info" className="mb-3">
                <div className="fw-bold mb-1">Why not eligible?</div>
                <ul className="mb-2">
                  {eligibilityData.reasons.map((reason, index) => (
                    <li key={index}>{reason}</li>
                  ))}
                </ul>
                <div className="fw-bold mb-1">How to meet requirements:</div>
                <ul className="mb-0">
                  {eligibilityData.graduationEligible !== undefined ? (
                    <>
                      <li>Complete all graduation requirements through the respective systems</li>
                      <li>Ensure all case logs, evaluations, and exams are properly recorded</li>
                      <li>Contact your program director for graduation approval</li>
                    </>
                  ) : (
                    <>
                      <li>Add case logs through the case log system</li>
                      <li>Record in-service exam results in the "In-Service Exams" tab</li>
                      <li>Complete required evaluations through the evaluation system</li>
                    </>
                  )}
                </ul>
              </Alert>
            )}
            {eligibilityData.warningReasons && eligibilityData.warningReasons.length > 0 && (
              <Alert variant="warning" className="mb-0">
                <div className="fw-bold mb-1">Promotion Warnings (will not block promotion)</div>
                <ul className="mb-0">
                  {eligibilityData.warningReasons.map((reason, index) => (
                    <li key={index}>{reason}</li>
                  ))}
                </ul>
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-light border-0">
        <Button variant="secondary" onClick={() => setShowEligibilityModal(false)}>
          Close
        </Button>
        {eligibilityData && eligibilityData.eligible && (
          <Button 
            variant="success" 
            onClick={() => {
              setShowEligibilityModal(false);
              if (eligibilityData.graduationEligible !== undefined) {
                // Show graduation confirmation modal
                console.log('🎓 Opening graduation modal for resident ID:', eligibilityData.residentId);
                console.log('👥 Available residents:', residents.map(r => ({ id: r.id, name: `${r.firstName} ${r.lastName}` })));
                
                // Find the full resident object from the residents list
                const resident = residents.find(r => r.id === eligibilityData.residentId);
                console.log('🔍 Found resident:', resident);
                
                setSelectedResident(resident || { id: eligibilityData.residentId });
                setShowGraduateModal(true);
              } else {
                // Find the full resident object from the residents list
                const resident = residents.find(r => r.id === eligibilityData.residentId);
                setSelectedResident(resident || { id: eligibilityData.residentId });
                setShowPromoteModal(true);
              }
            }}
          >
            <FaArrowUp className="me-2" /> 
            {eligibilityData.graduationEligible !== undefined ? 'Graduate Now' : 'Promote Now'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );

  const renderCopyRequirementsModal = () => {
    const candidateYears = [
      selectedAcademicYear - 2,
      selectedAcademicYear - 1,
      selectedAcademicYear,
      selectedAcademicYear + 1,
      selectedAcademicYear + 2
    ].filter((y) => Number.isFinite(y));

    const sourceYearOptions = Array.from(new Set(candidateYears)).map((y) => ({
      value: y,
      label: `${y}-${y + 1}`
    }));

    const hasAnyCopyTarget = !!copyRequirementsMode.copyPgySpecific || !!copyRequirementsMode.copyProgramWideEvidence;
    const disabledReason = !hasAnyCopyTarget ? 'Select at least one section to copy.' : '';

    return (
      <Modal
        show={showCopyRequirementsModal}
        onHide={() => setShowCopyRequirementsModal(false)}
        size="md"
        centered
      >
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-100">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>Copy Promotion Requirements</span>
              <div className="text-hl"></div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 pb-2">
          <Alert variant="info" className="small mb-3">
            This will copy settings from a source academic year into{' '}
            <b>
              {selectedAcademicYear}-{selectedAcademicYear + 1}
            </b>
            .
          </Alert>

          <Form onSubmit={handleCopyRequirements}>
            <Form.Group className="mb-3">
              <FloatingLabel controlId="copy-source-academic-year" label="Source academic year" className="required-field">
                <Form.Select
                  value={copyRequirementsSourceAcademicYear}
                  onChange={(e) => setCopyRequirementsSourceAcademicYear(parseInt(e.target.value, 10))}
                  required
                >
                  {sourceYearOptions.map((opt) => (
                    <option key={opt.value} value={opt.value} disabled={opt.value === selectedAcademicYear}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
              {copyRequirementsSourceAcademicYear === selectedAcademicYear ? (
                <div className="text-muted small mt-1">Choose a different year to copy from.</div>
              ) : null}
            </Form.Group>

            <div className="mb-3">
              <div className="small text-muted text-uppercase fw-semibold mb-2">Sections to copy</div>
              <div className="d-flex flex-column gap-2">
                <Form.Check
                  type="switch"
                  id="copy-pgy-specific-switch"
                  label="PGY-level specific Checklist"
                  checked={copyRequirementsMode.copyPgySpecific}
                  onChange={(e) =>
                    setCopyRequirementsMode((m) => ({ ...m, copyPgySpecific: e.target.checked }))
                  }
                />
                <Form.Check
                  type="switch"
                  id="copy-program-wide-switch"
                  label="Program-wide Checklist"
                  checked={copyRequirementsMode.copyProgramWideEvidence}
                  onChange={(e) =>
                    setCopyRequirementsMode((m) => ({ ...m, copyProgramWideEvidence: e.target.checked }))
                  }
                />
              </div>
            </div>

            {copyRequirementsError ? (
              <Alert variant="danger" className="small">
                {copyRequirementsError}
              </Alert>
            ) : null}

            {disabledReason ? <div className="text-danger small mb-3">{disabledReason}</div> : null}

            <Modal.Footer className="px-0 border-top-0">
              <Button variant="light" size="sm" onClick={() => setShowCopyRequirementsModal(false)} disabled={copyRequirementsLoading}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={
                  copyRequirementsLoading ||
                  !hasAnyCopyTarget ||
                  copyRequirementsSourceAcademicYear === selectedAcademicYear
                }
              >
                {copyRequirementsLoading ? <Spinner animation="border" size="sm" /> : 'Copy Settings'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>
    );
  };

  const renderRequirementsModal = () => {
    const showProgramWidePrefillNotice = !isEditingRequirement && hasAnyProgramWideCategories(requirementForm);
    return (
    <Modal show={showRequirementsModal} onHide={() => setShowRequirementsModal(false)} size="lg">
      <Modal.Header closeButton className="border-bottom-0 px-4">
        <Modal.Title className="w-100">
          <div className="d-flex flex-column justify-content-start align-items-start">
            <span>Set Promotion Checklist</span>
            <div className="text-hl"></div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 pb-2">
        <Form onSubmit={saveRequirement}>
          {requirementsModalNav === 'pgy-specific' ? (
          <Row>
            <Col md={4} className="mb-3">
              <Form.Group>
                <FloatingLabel controlId="floatingInput" label="PGY Level" className="required-field">
                  <Form.Select
                    value={requirementForm.pgyLevel || 1}
                    onChange={(e) => {
                      setPgySpecificDirty(true);
                      setRequirementForm({ ...requirementForm, pgyLevel: parseInt(e.target.value) });
                    }}
                    required
                  >
                    {(pgyLevelOptions.length > 0 ? pgyLevelOptions : Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `PGY-${i + 1}` }))).map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
                {requirementForm.pgyLevel === 5 && (
                  <div className="mt-2 p-2 bg-warning bg-opacity-10 border border-warning rounded">
                    <small className="text-warning">
                      <strong>🎓 Graduation Requirements:</strong> PGY 5 requirements are automatically used as graduation requirements for residents at maximum PGY level.
                    </small>
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={4} className="mb-3">
              <Form.Group>
                <FloatingLabel controlId="floatingInput" label="Academic Year" className="required-field">
                <Form.Control
                  as="select"
                  value={requirementForm.academicYear || new Date().getFullYear()}
                  onChange={(e) => {
                    setPgySpecificDirty(true);
                    setRequirementForm({ ...requirementForm, academicYear: parseInt(e.target.value) });
                  }}
                  required
                >
                  <option value={currentYear}>{currentYear}-{currentYear + 1}</option>
                  <option value={currentYear + 1}>{currentYear + 1}-{currentYear + 2}</option>
                  <option value={currentYear - 1}>{currentYear - 1}-{currentYear}</option>
                  </Form.Control>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col md={4} className="mb-3">  
     <Form.Group>
            <FloatingLabel controlId="floatingInput" label="Case Logs Required" className="required-field">
            <Form.Control
              type="number"
              min="0"
              value={requirementForm.requiredCaseLogs ?? ''}
              onChange={e => {
                setPgySpecificDirty(true);
                setRequirementForm({ ...requirementForm, requiredCaseLogs: e.target.value === '' ? '' : parseInt(e.target.value) });
              }}
              />
            </FloatingLabel>
          </Form.Group>
            </Col>
            <Col md={12} className="mb-3">
              <Form.Group>
                <FloatingLabel controlId="floatingInput" label="Biannual Evaluation Template" className="required-field">
                <Form.Control
                  as="select"
                  value={requirementForm.evaluationTemplateId || ''}
                  onChange={(e) => {
                    setPgySpecificDirty(true);
                    setRequirementForm({ ...requirementForm, evaluationTemplateId: e.target.value });
                  }}
                >
                  <option value="">Select Template</option>
                  {evaluationTemplates.map(template => (
                    <option key={template.id} value={template.id}>{template.title}</option>
                  ))}
                  </Form.Control>
                </FloatingLabel>
                <div className="p-3 bg-info rounded-2 mt-2">
                <Form.Text className="text-muted">
                  <strong>Biannual Recurrence:</strong> Evaluations will be automatically created at 3 months (October 1st, "Midterm") and 9 months (April 1st, "Term") from the academic year start (July 1st). 
                  <br />
                  <strong>Title:</strong> Will be set as "{requirementForm.academicYear}-{requirementForm.academicYear + 1} PGY-{requirementForm.pgyLevel} Midterm Evaluation" and "{requirementForm.academicYear}-{requirementForm.academicYear + 1} PGY-{requirementForm.pgyLevel} Term Evaluation"
                  <br />
                  <strong>Assignment:</strong> All evaluations will be assigned to the Program Director for all residents in this PGY level.
                </Form.Text>
                </div>
              </Form.Group>
            </Col>
            <Col md={6} className="mb-3">
                      <Form.Group>
            <Form.Check
              type="checkbox"
              label="Requires In-Service Exam"
              checked={requirementForm.requiresInServiceExam}
              onChange={(e) => {
                setPgySpecificDirty(true);
                setRequirementForm({ ...requirementForm, requiresInServiceExam: e.target.checked });
              }}
            />
          </Form.Group>
            </Col>
            <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Check
              type="checkbox"
              label="In-Service Exam Pass Required"
              checked={requirementForm.inServiceExamPassRequired}
              onChange={(e) => {
                setPgySpecificDirty(true);
                setRequirementForm({ ...requirementForm, inServiceExamPassRequired: e.target.checked });
              }}
            />
          </Form.Group>
            </Col>
          </Row>
          ) : (
                    <div style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                    <h6 className="mb-3 fw-semibold">Program-wide Required Checklist</h6>
          {showProgramWidePrefillNotice && (
            <Alert variant="secondary" className="py-2 small mb-3">
              Loaded existing program-wide evidence items for this academic year. You can edit the list below; saving updates every PGY row and graduation checks. Completion counts reflect each resident’s latest submission per item.
            </Alert>
          )}
          {[
            ['licensesCertifications', 'Licenses & Certifications'],
            ['onboardingRequirements', 'Onboarding Requirements'],
            ['trainingCertifications', 'Training Certifications'],
            ['vaccinations', 'Vaccinations'],
            ['scholarlyActivity', 'Scholarly Activity']
          ].map(([field, label]) => {
            const itemCount = (requirementForm[field] || []).length;
            const draft = categoryDrafts[field];
            const showComposer = draft.enabled && draft.composerOpen;
            return (
              <div key={field} className="mb-3 p-3 border rounded">
                <div className="d-flex flex-row align-items-center justify-content-between gap-2 mb-1">
                  <div className="d-flex flex-column w-100">
                    <div className="d-flex flex-row align-items-center justify-content-between gap-2">
                      <h6 className="fw-semibold">{label}</h6>
                      <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        <span className="small text-muted text-nowrap">{draft.enabled ? 'On' : 'Off'}</span>
                        <Form.Check
                          type="switch"
                          id={`promo-cat-${field}`}
                          aria-label={`${label} requirements`}
                          checked={draft.enabled}
                          onChange={(e) => {
                            const on = e.target.checked;
                            const count = (requirementForm[field] || []).length;
                            setProgramWideDirty(true);
                            setCategoryDrafts((prev) => ({
                              ...prev,
                              [field]: {
                                ...prev[field],
                                enabled: on,
                                composerOpen: on && count === 0
                              }
                            }));
                            if (!on && field === 'vaccinations') {
                              setRequirementForm((prev) => ({
                                ...prev,
                                vaccinationCardPgyLevels: []
                              }));
                            }
                          }}
                        />
                      </div>
                    </div>
                    <p className="text-muted small mb-0">{CATEGORY_DESCRIPTIONS[field]}</p>
                  </div>
                  

                </div>
                {itemCount === 0 && showComposer && (
                  <div className="mt-2 pt-2 border-top">
                    <Row className="g-2 align-items-end">
                      {field === 'onboardingRequirements' && (
                        <>
                          <Col md={3}>
                            <Form.Label className="small text-muted mb-0">Type</Form.Label>
                            <Form.Select
                              size="sm"
                              value={categoryDrafts[field].onboardingType}
                              onChange={(e) => setCategoryDrafts((prev) => ({ ...prev, [field]: { ...prev[field], onboardingType: e.target.value, onboardingId: '' } }))}
                            >
                              <option value="package">Package</option>
                              <option value="document">Document</option>
                            </Form.Select>
                          </Col>
                          <Col md={5}>
                            <Form.Label className="small text-muted mb-0">Select</Form.Label>
                            <Form.Select
                              size="sm"
                              value={categoryDrafts[field].onboardingId || ''}
                              onChange={(e) => {
                                const selectedId = e.target.value;
                                const selectedRecord = categoryDrafts[field].onboardingType === 'package'
                                  ? onboardingPackages.find((pkg) => String(pkg.id) === String(selectedId))
                                  : onboardingDocuments.find((doc) => String(doc.id) === String(selectedId));
                                setCategoryDrafts((prev) => ({
                                  ...prev,
                                  [field]: {
                                    ...prev[field],
                                    onboardingId: selectedId,
                                    selectedOption: selectedRecord ? (selectedRecord.name || selectedRecord.title) : prev[field].selectedOption
                                  }
                                }));
                              }}
                            >
                              <option value="">Select {categoryDrafts[field].onboardingType}</option>
                              {(categoryDrafts[field].onboardingType === 'package' ? onboardingPackages : onboardingDocuments).map((entry) => (
                                <option key={entry.id} value={entry.id}>
                                  {entry.name || entry.title}
                                </option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col md={4} className="d-flex align-items-end gap-2 pb-1 flex-wrap">
                            <span className="small text-muted">Force document</span>
                            <span className="small text-muted">{categoryDrafts[field].forceRequirement ? 'On' : 'Off'}</span>
                            <Form.Check
                              type="switch"
                              id={`promo-force-${field}`}
                              aria-label="Force document requirement on resident profile"
                              checked={!!categoryDrafts[field].forceRequirement}
                              onChange={(e) => setCategoryDrafts((prev) => ({ ...prev, [field]: { ...prev[field], forceRequirement: e.target.checked } }))}
                            />
                          </Col>
                          <Col md={12} className="mt-2">
                            <Form.Label className="small text-muted mb-1">Required for PGY levels (none = all)</Form.Label>
                            <div className="d-flex flex-wrap gap-3">
                              {(pgyLevelOptions.length > 0
                                ? pgyLevelOptions
                                : Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `PGY-${i + 1}` }))
                              ).map((opt) => (
                                <Form.Check
                                  key={opt.value}
                                  type="checkbox"
                                  id={`promo-onb-pgy-${field}-${opt.value}`}
                                  label={opt.label}
                                  checked={(categoryDrafts[field].requiredPgyLevels || []).includes(opt.value)}
                                  onChange={(e) => {
                                    setProgramWideDirty(true);
                                    const set = new Set(categoryDrafts[field].requiredPgyLevels || []);
                                    if (e.target.checked) set.add(opt.value);
                                    else set.delete(opt.value);
                                    setCategoryDrafts((prev) => ({
                                      ...prev,
                                      [field]: { ...prev[field], requiredPgyLevels: [...set].sort((a, b) => a - b) }
                                    }));
                                  }}
                                />
                              ))}
                            </div>
                          </Col>
                        </>
                      )}
                      {field === 'scholarlyActivity' && (
                        <>
                          <Col md={4}>
                            <Form.Label className="small text-muted mb-0">Requirement name</Form.Label>
                            <Form.Control
                              size="sm"
                              placeholder={SCHOLARLY_DEFAULT_NAME}
                              value={categoryDrafts[field].scholarlyRequirementName ?? ''}
                              onChange={(e) =>
                                setCategoryDrafts((prev) => ({
                                  ...prev,
                                  [field]: { ...prev[field], scholarlyRequirementName: e.target.value }
                                }))
                              }
                            />
                          </Col>
                          <Col md={8}>
                            <Form.Label className="small text-muted mb-0">Description for residents</Form.Label>
                            <Form.Control
                              size="sm"
                              placeholder="Optional instructions"
                              value={categoryDrafts[field].info}
                              onChange={(e) => setCategoryDrafts((prev) => ({ ...prev, [field]: { ...prev[field], info: e.target.value } }))}
                            />
                          </Col>
                          <Col md={12} className="mt-2">
                            <div className="small text-muted mb-1">Complete by (choose one or both)</div>
                            <div className="d-flex flex-wrap align-items-center gap-3">
                              <Form.Check
                                type="checkbox"
                                id={`sch-due-pgy-${field}`}
                                label="End of PGY year"
                                checked={!!categoryDrafts[field].dueByEndOfPgy}
                                onChange={(e) => {
                                  setProgramWideDirty(true);
                                  setCategoryDrafts((prev) => ({
                                    ...prev,
                                    [field]: { ...prev[field], dueByEndOfPgy: e.target.checked }
                                  }));
                                }}
                              />
                              {categoryDrafts[field].dueByEndOfPgy ? (
                                <Form.Select
                                  size="sm"
                                  style={{ maxWidth: 140 }}
                                  value={categoryDrafts[field].dueByEndOfPgyLevel || 1}
                                  onChange={(e) => {
                                    setProgramWideDirty(true);
                                    setCategoryDrafts((prev) => ({
                                      ...prev,
                                      [field]: { ...prev[field], dueByEndOfPgyLevel: parseInt(e.target.value, 10) || 1 }
                                    }));
                                  }}
                                >
                                  {(pgyLevelOptions.length > 0
                                    ? pgyLevelOptions
                                    : Array.from({ length: maxPGYLevel || 5 }, (_, i) => ({
                                        value: i + 1,
                                        label: `PGY-${i + 1}`
                                      }))
                                  ).map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label || `PGY-${opt.value}`}
                                    </option>
                                  ))}
                                </Form.Select>
                              ) : null}
                              <Form.Check
                                type="checkbox"
                                id={`sch-due-grad-${field}`}
                                label="Before graduation"
                                checked={!!categoryDrafts[field].dueBeforeGraduation}
                                onChange={(e) => {
                                  setProgramWideDirty(true);
                                  setCategoryDrafts((prev) => ({
                                    ...prev,
                                    [field]: { ...prev[field], dueBeforeGraduation: e.target.checked }
                                  }));
                                }}
                              />
                            </div>
                          </Col>
                        </>
                      )}
                      {field !== 'onboardingRequirements' && field !== 'scholarlyActivity' && (
                        <>
                          <Col md={3}>
                            <Form.Label className="small text-muted mb-0">Option</Form.Label>
                            <Form.Select
                              size="sm"
                              value={categoryDrafts[field].selectedOption}
                              onChange={(e) => setCategoryDrafts((prev) => ({ ...prev, [field]: { ...prev[field], selectedOption: e.target.value } }))}
                            >
                              {(CATEGORY_OPTIONS[field] || ['Other']).map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </Form.Select>
                          </Col>
                          
                            {categoryDrafts[field].selectedOption === 'Other' ? (
                            <Col md={3}>
                            <Form.Label className="small text-muted mb-0">Name</Form.Label>
                              <Form.Control
                                size="sm"
                                placeholder="Custom name"
                                value={categoryDrafts[field].otherOption}
                                onChange={(e) => setCategoryDrafts((prev) => ({ ...prev, [field]: { ...prev[field], otherOption: e.target.value } }))}
                              /></Col>
                            ) : null}
                          
                          <Col md={4}>
                            <Form.Label className="small text-muted mb-0">Description</Form.Label>
                            <Form.Control
                              size="sm"
                              placeholder="Description for residents"
                              value={categoryDrafts[field].info}
                              onChange={(e) => setCategoryDrafts((prev) => ({ ...prev, [field]: { ...prev[field], info: e.target.value } }))}
                            />
                          </Col>
                        </>
                      )}
                      {(field === 'trainingCertifications' || field === 'vaccinations') && (
                        <Col md={categoryDrafts[field].selectedOption === 'Other' ? 2: 5}>
                          <Form.Label className="small text-muted mb-0">Recurrence</Form.Label>
                          <Form.Select
                            size="sm"
                            value={categoryDrafts[field].recurrenceType}
                            onChange={(e) =>
                              setCategoryDrafts((prev) => ({
                                ...prev,
                                [field]: { ...prev[field], recurrenceType: e.target.value }
                              }))
                            }
                          >
                            <option value="yearly_until_graduation">Yearly until graduation</option>
                            <option value="until_complete">Until complete</option>
                          </Form.Select>
                        </Col>
                      )}
                      {field === 'licensesCertifications' && (
                        <Col md={6}>
                          <Form.Label className="small text-muted mb-0">Recurrence</Form.Label>
                          <Form.Select
                            size="sm"
                            value={categoryDrafts[field].recurrenceType}
                            onChange={(e) =>
                              setCategoryDrafts((prev) => ({
                                ...prev,
                                [field]: { ...prev[field], recurrenceType: e.target.value }
                              }))
                            }
                          >
                            <option value="yearly_until_graduation">Yearly until graduation</option>
                            <option value="until_expiration_until_graduation">When it expires until graduation</option>
                          </Form.Select>
                        </Col>
                      )}
                      <Col md={12} className="d-flex flex-row align-items-center gap-2 justify-content-between mt-2">
                        <div className="d-flex align-items-center gap-2">
                          <span className="small text-muted">Required</span>
                          <span className="small text-muted">{categoryDrafts[field].required ? 'On' : 'Off'}</span>
                          <Form.Check
                            type="switch"
                            id={`promo-req-${field}`}
                            aria-label="Required for residents"
                            checked={categoryDrafts[field].required}
                            onChange={(e) => setCategoryDrafts((prev) => ({ ...prev, [field]: { ...prev[field], required: e.target.checked } }))}
                          />
                        </div>
                    
                      <div className="d-flex flex-row align-items-center gap-2">
                           <Button type="button" variant="primary" size="sm" onClick={() => addCategoryItem(field)}>
                          {itemCount === 0 ? 'Add requirement' : 'Add to list'}
                        </Button>
                        {itemCount > 0 && (
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="text-decoration-none p-0"
                            onClick={() => setCategoryDrafts((prev) => ({ ...prev, [field]: { ...prev[field], composerOpen: false } }))}
                          >
                            Cancel
                          </Button>
                        )}
                        </div>
                      </Col>
                    </Row>
                  </div>
                )}
                {field === 'vaccinations' && draft.enabled && (
                  <div className="mt-3 p-3 border rounded" style={{ borderColor: 'rgba(13, 110, 253, 0.35)' }}>
                      <div className="d-flex align-items-start gap-2 justify-content-start">
                        <span className="d-inline-flex align-items-center justify-content-center rounded-2 bg-primary p-2 me-1">
                          <FaSyringe size={16} className="m-1" />
                        </span>
                        <div className="d-flex flex-column mb-2">
                          <div className="d-flex flex-row align-items-center justify-content-between gap-2">
                            <h6 className="fw-semibold mb-0">Structured vaccination card</h6>
                           <div className="d-flex flex-row align-items-center justify-content-end gap-2">
                            <span className="small text-muted me-2">Require</span>
                            <Form.Check
                              type="switch"
                              id="promo-vaccination-card"
                              checked={(requirementForm.vaccinationCardPgyLevels || []).length > 0}
                              onChange={(e) => {
                                setProgramWideDirty(true);
                                if (e.target.checked) {
                                  setRequirementForm((prev) => ({
                                    ...prev,
                                    vaccinationCardPgyLevels: maxPGYLevel >= 1 ? [1] : []
                                  }));
                                } else {
                                  setRequirementForm((prev) => ({
                                    ...prev,
                                    vaccinationCardPgyLevels: []
                                  }));
                                }
                              }}
                            />
                            </div>
                          </div>  
                            <p className="text-muted small mb-0">
                              Only residents in the PGY levels you select below must complete the full structured card.
                              Other PGYs still complete individual vaccination activities; approved doses are merged into the card for viewing from each activity.
                            </p>
                        </div>
                      </div>
                    {(requirementForm.vaccinationCardPgyLevels || []).length > 0 && (
                      <>
                      <div className="d-flex flex-row align-items-center  justify-content-between gap-2">
                        <div className="d-flex flex-wrap gap-3 align-items-center">
                          <span className="small text-muted ms-2">Require for PGY:</span>
                          {(pgyLevelOptions.length > 0
                            ? pgyLevelOptions
                            : Array.from({ length: maxPGYLevel || 5 }, (_, i) => ({
                                value: i + 1,
                                label: `PGY-${i + 1}`
                              }))
                          ).map((opt) => {
                            const lvl = opt.value;
                            return (
                              <Form.Check
                                key={`vcc-pgy-${lvl}`}
                                type="checkbox"
                                id={`vcc-pgy-checkbox-${lvl}`}
                                label={opt.label || `PGY-${lvl}`}
                                checked={(requirementForm.vaccinationCardPgyLevels || []).includes(lvl)}
                                onChange={(e) => {
                                  setProgramWideDirty(true);
                                  setRequirementForm((prev) => {
                                    const prevLvls = prev.vaccinationCardPgyLevels || [];
                                    let newLevels;
                                    if (e.target.checked) {
                                      newLevels = [...prevLvls, lvl].sort((a, b) => a - b);
                                    } else {
                                      newLevels = prevLvls.filter((val) => val !== lvl);
                                    }
                                    return {
                                      ...prev,
                                      vaccinationCardPgyLevels: newLevels
                                    };
                                  });
                                }}
                                className="me-2"
                              />
                            );
                          })}
                        </div>
                        <div className="text-muted text-end" style={{ minWidth: 140 }}>
                          {renderEvidenceCompletionCell('vaccinationCard', VACCINATION_CARD_ITEM_NAME)}
                        </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {itemCount > 0 && (
                  <>
                    <ListGroup className="mt-2 mb-0">
                      {(requirementForm[field] || []).map((item, idx) => (
                        <React.Fragment key={`${field}-${idx}`}>
                          <ListGroup.Item className="d-flex align-items-center justify-content-between py-2 px-3">
                            <div className="d-flex flex-column flex-md-row align-items-md-center gap-4">
                              <div className="d-flex flex-column flex-grow-1">
                                <span className="fw-semibold">{item.name}</span>
                                {field === 'onboardingRequirements'
                                  ? (
                                      <>
                                        <span className="small text-muted">
                                          Type: {item.onboardingType || 'N/A'} | Force: {item.forceRequirement ? 'On' : 'Off'}
                                        </span>
                                      </>
                                    )
                                  : null}
                                <span className="small text-muted">{item.info || ''}</span>
                                </div>
                              <div className="text-muted small" style={{ minWidth: 180 }}>
                                {field === 'onboardingRequirements'
                                  ? (
                                      <>
                                        <div>
                                          {Array.isArray(item.requiredPgyLevels) && item.requiredPgyLevels.length > 0
                                            ? `PGY level: ${item.requiredPgyLevels.map((n) => `PGY-${n}`).join(', ')}`
                                            : 'PGY: All levels'}
                                        </div>
                                        {item.recurrenceType ? (
                                          <div>
                                            Recurrence:{' '}
                                            {item.recurrenceType === 'yearly_until_graduation'
                                              ? 'Yearly until graduation'
                                              : item.recurrenceType === 'until_expiration_until_graduation'
                                                ? 'When it expires until graduation'
                                                : item.recurrenceType === 'until_complete'
                                                  ? 'Until complete'
                                                  : item.recurrenceType}
                                          </div>
                                        ) : null}
                                      </>
                                    )
                                  : field === 'scholarlyActivity' ? (
                                    <>
                                      <div>Turn in evidence once when complete.</div>
                                      {formatScholarlyDueSummary(item) ? (
                                        <div>Due: {formatScholarlyDueSummary(item)}</div>
                                      ) : null}
                                    </>
                                  ) : (
                                    <>
                                      {item.recurrenceType ? (
                                        <div>
                                          Recurrence:{' '}
                                          {item.recurrenceType === 'yearly_until_graduation'
                                            ? 'Yearly until graduation'
                                            : item.recurrenceType === 'until_expiration_until_graduation'
                                              ? 'When it expires until graduation'
                                              : item.recurrenceType === 'until_complete'
                                                ? 'Until complete'
                                                : item.recurrenceType}
                                        </div>
                                      ) : null}
                                    </>
                                  )}
                              </div>
                             
                        
                            </div>
                            <div className="d-flex flex-row align-items-center gap-4">
                               <div className="d-flex flex-row align-items-center">
                                <i className={`bi bi-${item.required ? 'braces-asterisk text-danger' : 'braces text-muted'} me-2`}></i>
                                <span className="small">{}{item.required ? 'Yes' : 'No'}</span>
                              </div>
                              <div>
                                {renderEvidenceCompletionCell(field, item.name, item)}
                              </div>  
                            <Button
                              variant="link-dng"
                              size="sm"
                              className="rounded-pill py-0 px-1"
                              onClick={() => removeCategoryItem(field, idx)}
                            >
                              <i className="bi bi-x"></i>
                            </Button>
                            </div>
                          </ListGroup.Item>

                          {showComposer && idx === 0 && (
                            <ListGroup.Item className="bg-light py-3 px-3">
                              <Row className="g-2 align-items-end">
                                {field === 'onboardingRequirements' && (
                                  <>
                                    <Col md={2}>
                                      <Form.Label className="small text-muted mb-0">Type</Form.Label>
                                      <Form.Select
                                        size="sm"
                                        value={categoryDrafts[field].onboardingType}
                                        onChange={(e) => setCategoryDrafts((prev) => ({ ...prev, [field]: { ...prev[field], onboardingType: e.target.value, onboardingId: '' } }))}
                                      >
                                        <option value="package">Package</option>
                                        <option value="document">Document</option>
                                      </Form.Select>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Label className="small text-muted mb-0">Select</Form.Label>
                                      <Form.Select
                                        size="sm"
                                        value={categoryDrafts[field].onboardingId || ''}
                                        onChange={(e) => {
                                          const selectedId = e.target.value;
                                          const selectedRecord = categoryDrafts[field].onboardingType === 'package'
                                            ? onboardingPackages.find((pkg) => String(pkg.id) === String(selectedId))
                                            : onboardingDocuments.find((doc) => String(doc.id) === String(selectedId));
                                          setCategoryDrafts((prev) => ({
                                            ...prev,
                                            [field]: {
                                              ...prev[field],
                                              onboardingId: selectedId,
                                              selectedOption: selectedRecord ? (selectedRecord.name || selectedRecord.title) : prev[field].selectedOption
                                            }
                                          }));
                                        }}
                                      >
                                        <option value="">Select {categoryDrafts[field].onboardingType}</option>
                                        {(categoryDrafts[field].onboardingType === 'package' ? onboardingPackages : onboardingDocuments).map((entry) => (
                                          <option key={entry.id} value={entry.id}>
                                            {entry.name || entry.title}
                                          </option>
                                        ))}
                                      </Form.Select>
                                    </Col>
                                    <Col md={4} className="d-flex align-items-end gap-2 pb-1 flex-wrap">
                                      <span className="small text-muted">Force document</span>
                                      <span className="small text-muted">{categoryDrafts[field].forceRequirement ? 'On' : 'Off'}</span>
                                      <Form.Check
                                        type="switch"
                                        id={`promo-force-${field}-dup`}
                                        aria-label="Force document requirement on resident profile"
                                        checked={!!categoryDrafts[field].forceRequirement}
                                        onChange={(e) => setCategoryDrafts((prev) => ({ ...prev, [field]: { ...prev[field], forceRequirement: e.target.checked } }))}
                                      />
                                    </Col>
                                    <Col md={12} className="mt-2">
                                      <Form.Label className="small text-muted mb-1">Required for PGY levels (none = all)</Form.Label>
                                      <div className="d-flex flex-wrap gap-3">
                                        {(pgyLevelOptions.length > 0
                                          ? pgyLevelOptions
                                          : Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `PGY-${i + 1}` }))
                                        ).map((opt) => (
                                          <Form.Check
                                            key={opt.value}
                                            type="checkbox"
                                            id={`promo-onb-pgy-dup-${field}-${opt.value}`}
                                            label={opt.label}
                                            checked={(categoryDrafts[field].requiredPgyLevels || []).includes(opt.value)}
                                            onChange={(e) => {
                                              setProgramWideDirty(true);
                                              const set = new Set(categoryDrafts[field].requiredPgyLevels || []);
                                              if (e.target.checked) set.add(opt.value);
                                              else set.delete(opt.value);
                                              setCategoryDrafts((prev) => ({
                                                ...prev,
                                                [field]: { ...prev[field], requiredPgyLevels: [...set].sort((a, b) => a - b) }
                                              }));
                                            }}
                                          />
                                        ))}
                                      </div>
                                    </Col>
                                  </>
                                )}
                                {field === 'scholarlyActivity' && (
                                  <>
                                    <Col md={4}>
                                      <Form.Label className="small text-muted mb-0">Requirement name</Form.Label>
                                      <Form.Control
                                        size="sm"
                                        placeholder={SCHOLARLY_DEFAULT_NAME}
                                        value={categoryDrafts[field].scholarlyRequirementName ?? ''}
                                        onChange={(e) =>
                                          setCategoryDrafts((prev) => ({
                                            ...prev,
                                            [field]: { ...prev[field], scholarlyRequirementName: e.target.value }
                                          }))
                                        }
                                      />
                                    </Col>
                                    <Col md={8}>
                                      <Form.Label className="small text-muted mb-0">Description for residents</Form.Label>
                                      <Form.Control
                                        size="sm"
                                        placeholder="Optional instructions"
                                        value={categoryDrafts[field].info}
                                        onChange={(e) => setCategoryDrafts((prev) => ({ ...prev, [field]: { ...prev[field], info: e.target.value } }))}
                                      />
                                    </Col>
                                    <Col md={12} className="mt-2">
                                      <div className="small text-muted mb-1">Complete by (choose one or both)</div>
                                      <div className="d-flex flex-wrap align-items-center gap-3">
                                        <Form.Check
                                          type="checkbox"
                                          id={`sch-due-pgy-dup-${field}`}
                                          label="End of PGY year"
                                          checked={!!categoryDrafts[field].dueByEndOfPgy}
                                          onChange={(e) => {
                                            setProgramWideDirty(true);
                                            setCategoryDrafts((prev) => ({
                                              ...prev,
                                              [field]: { ...prev[field], dueByEndOfPgy: e.target.checked }
                                            }));
                                          }}
                                        />
                                        {categoryDrafts[field].dueByEndOfPgy ? (
                                          <Form.Select
                                            size="sm"
                                            style={{ maxWidth: 140 }}
                                            value={categoryDrafts[field].dueByEndOfPgyLevel || 1}
                                            onChange={(e) => {
                                              setProgramWideDirty(true);
                                              setCategoryDrafts((prev) => ({
                                                ...prev,
                                                [field]: { ...prev[field], dueByEndOfPgyLevel: parseInt(e.target.value, 10) || 1 }
                                              }));
                                            }}
                                          >
                                            {(pgyLevelOptions.length > 0
                                              ? pgyLevelOptions
                                              : Array.from({ length: maxPGYLevel || 5 }, (_, i) => ({
                                                  value: i + 1,
                                                  label: `PGY-${i + 1}`
                                                }))
                                            ).map((opt) => (
                                              <option key={opt.value} value={opt.value}>
                                                {opt.label || `PGY-${opt.value}`}
                                              </option>
                                            ))}
                                          </Form.Select>
                                        ) : null}
                                        <Form.Check
                                          type="checkbox"
                                          id={`sch-due-grad-dup-${field}`}
                                          label="Before graduation"
                                          checked={!!categoryDrafts[field].dueBeforeGraduation}
                                          onChange={(e) => {
                                            setProgramWideDirty(true);
                                            setCategoryDrafts((prev) => ({
                                              ...prev,
                                              [field]: { ...prev[field], dueBeforeGraduation: e.target.checked }
                                            }));
                                          }}
                                        />
                                      </div>
                                    </Col>
                                  </>
                                )}
                                {field !== 'onboardingRequirements' && field !== 'scholarlyActivity' && (
                                  <>
                                    <Col md={2}>
                                      <Form.Label className="small text-muted mb-0">Option</Form.Label>
                                      <Form.Select
                                        size="sm"
                                        value={categoryDrafts[field].selectedOption}
                                        onChange={(e) => setCategoryDrafts((prev) => ({ ...prev, [field]: { ...prev[field], selectedOption: e.target.value } }))}
                                      >
                                        {(CATEGORY_OPTIONS[field] || ['Other']).map((option) => (
                                          <option key={option} value={option}>{option}</option>
                                        ))}
                                      </Form.Select>
                                    </Col>
                                   
                                      
                                      {categoryDrafts[field].selectedOption === 'Other' ? ( 
                                        <Col md={3}><Form.Label className="small text-muted mb-0">Name</Form.Label>
                                        <Form.Control
                                          size="sm"
                                          placeholder="Custom name"
                                          value={categoryDrafts[field].otherOption}
                                          onChange={(e) => setCategoryDrafts((prev) => ({ ...prev, [field]: { ...prev[field], otherOption: e.target.value } }))}
                                        /></Col>
                                      ) : null
                                      }
                                     {field === 'licensesCertifications' && (
                                  <Col md={ categoryDrafts[field].selectedOption === 'Other' ? 3:6}>
                                    <Form.Label className="small text-muted mb-0">Recurrence</Form.Label>
                                    <Form.Select
                                      size="sm"
                                      value={categoryDrafts[field].recurrenceType}
                                      onChange={(e) =>
                                        setCategoryDrafts((prev) => ({
                                          ...prev,
                                          [field]: { ...prev[field], recurrenceType: e.target.value }
                                        }))
                                      }
                                    >
                                      <option value="yearly_until_graduation">Yearly until graduation</option>
                                      <option value="until_expiration_until_graduation">When it expires until graduation</option>
                                    </Form.Select>
                                  </Col>
                                )}
                                    <Col md={categoryDrafts[field].selectedOption === 'Other' ? 4: 4}>
                                      <Form.Label className="small text-muted mb-0">Description</Form.Label>
                                      <Form.Control
                                        size="sm"
                                        placeholder="Description for residents"
                                        value={categoryDrafts[field].info}
                                        onChange={(e) => setCategoryDrafts((prev) => ({ ...prev, [field]: { ...prev[field], info: e.target.value } }))}
                                      />
                                    </Col>
                                  </>
                                )}
                                {(field === 'trainingCertifications' || field === 'vaccinations') && (
                                  <Col md={ categoryDrafts[field].selectedOption === 'Other' ? 3: 6}>
                                    <Form.Label className="small text-muted mb-0">Recurrence</Form.Label>
                                    <Form.Select
                                      size="sm"
                                      value={categoryDrafts[field].recurrenceType}
                                      onChange={(e) =>
                                        setCategoryDrafts((prev) => ({
                                          ...prev,
                                          [field]: { ...prev[field], recurrenceType: e.target.value }
                                        }))
                                      }
                                    >
                                      <option value="yearly_until_graduation">Yearly until graduation</option>
                                      <option value="until_complete">Until complete</option>
                                    </Form.Select>
                                  </Col>
                                )}
                               
                                <Col md={12} className="d-flex flex-row justify-content-between align-items-center gap-2 mt-2">
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="small text-muted">Required</span>
                                    <span className="small text-muted">{categoryDrafts[field].required ? 'On' : 'Off'}</span>
                                    <Form.Check
                                      type="switch"
                                      id={`promo-req-${field}-dup`}
                                      aria-label="Required for residents"
                                      checked={categoryDrafts[field].required}
                                      onChange={(e) => setCategoryDrafts((prev) => ({ ...prev, [field]: { ...prev[field], required: e.target.checked } }))}
                                    />
                                  </div>
                                  <div className="d-flex flex-row align-items-center gap-2">
                                  <Button type="button" variant="primary" size="sm" onClick={() => addCategoryItem(field)}>
                                    {itemCount === 0 ? 'Add requirement' : 'Add to list'}
                                  </Button>
                                  {itemCount > 0 && (
                                    <Button
                                      type="button"
                                      variant="link"
                                      size="sm"
                                      className="text-decoration-none p-0"
                                      onClick={() => setCategoryDrafts((prev) => ({ ...prev, [field]: { ...prev[field], composerOpen: false } }))}
                                    >
                                      Cancel
                                    </Button>
                                  )}
                                  </div>
                                </Col>
                                
                              </Row>
                            </ListGroup.Item>
                          )}
                        </React.Fragment>
                      ))}
                    </ListGroup>
                    {draft.enabled && !draft.composerOpen && (
                      <div className="d-flex justify-content-end mt-2">
                        <Button
                          type="button"
                          variant="outline-primary"
                          size="sm"
                          onClick={() => setCategoryDrafts((prev) => ({
                            ...prev,
                            [field]: {
                              ...prev[field],
                              enabled: true,
                              composerOpen: true,
                              onboardingId: field === 'onboardingRequirements' ? '' : prev[field].onboardingId
                            }
                          }))}
                        >
                          Add another
                        </Button>
                      </div>
                    )}
                  </>
                )}
                
              </div>
            );
          })}
                  </div>
          )}
          <div className="d-flex justify-content-end mt-3 pt-3 border-top">
            <Button type="submit" variant="primary" size="sm">Save requirements</Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
    );
  };

  const renderInServiceModal = () => (
    <Modal show={showInServiceModal} onHide={() => {
      setShowInServiceModal(false);
      resetInServiceForm();
      setPdfFile(null);
    }} size="lg">
      <Modal.Header closeButton className="border-bottom-0 px-4">
        <Modal.Title className="w-100">
          <div className="d-flex flex-column justify-content-start align-items-start">
            <span>{isEditingExam ? 'Edit In-Service Exam' : 'Record In-Service Exam'}</span>
            <div className="text-hl"></div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4">
        <Form onSubmit={saveInServiceExam} encType="multipart/form-data">
          <Row>
            <Col md={12} className="mb-3">
                      <Form.Group>
            <FloatingLabel controlId="floatingInput" label="Resident" className="required-field">
            <Form.Control
              as="select"
              value={inServiceForm.residentId || ''}
              onChange={(e) => setInServiceForm({...inServiceForm, residentId: e.target.value})}
              required
              disabled={isEditingExam}
            >
              <option value="">Select Resident</option>
              {residents.map(resident => (
                <option key={resident.id} value={resident.id}>
                  {resident.firstName} {resident.lastName} (PGY-{resident.year})
                </option>
              ))}
            </Form.Control>
            </FloatingLabel>
          </Form.Group>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Group>
                <FloatingLabel controlId="floatingInput" label="Exam Date" className="required-field">
                <Form.Control
                  type="date"
                  value={inServiceForm.examDate || ''}
                  onChange={(e) => setInServiceForm({...inServiceForm, examDate: e.target.value})}
                  required
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Group>
                <FloatingLabel controlId="floatingInput" label="Academic Year" className="required-field">
                <Form.Control
                  as="select"
                  value={inServiceForm.academicYear || new Date().getFullYear()}
                  onChange={(e) => setInServiceForm({...inServiceForm, academicYear: parseInt(e.target.value)})}
                  required
                  disabled={isEditingExam}
                >
                  <option value={currentYear}>{currentYear}-{currentYear + 1}</option>
                  <option value={currentYear + 1}>{currentYear + 1}-{currentYear + 2}</option>
                  <option value={currentYear - 1}>{currentYear - 1}-{currentYear}</option>
                </Form.Control>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col md={4} className="mb-3">
              <Form.Group>
                <FloatingLabel controlId="floatingInput" label="PGY Level" className="required-field">
                <Form.Control
                  type="number"
                  min="1"
                  max={maxPGYLevel}
                  value={inServiceForm.pgyLevel || 1}
                  onChange={(e) => setInServiceForm({...inServiceForm, pgyLevel: parseInt(e.target.value)})}
                  required
                  disabled={isEditingExam}
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col md={4} className="mb-3">
              <Form.Group>
                <FloatingLabel controlId="floatingInput" label="Result" className="required-field">
                <Form.Control
                  as="select"
                  value={inServiceForm.result || 'pass'}
                  onChange={(e) => setInServiceForm({...inServiceForm, result: e.target.value})}
                  required
                >
                  <option value="pass">Pass</option>
                  <option value="fail">Fail</option>
                </Form.Control>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col md={4} className="mb-3"> 
            <Form.Group>
            <FloatingLabel controlId="floatingInput" label="Score (Optional)" className="required-field">
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={inServiceForm.score || ''}
              onChange={(e) => setInServiceForm({...inServiceForm, score: e.target.value})}
            />
            </FloatingLabel>
          </Form.Group>   
            </Col>
            <Col md={12} className="mb-3">
            <Form.Group>
            <FloatingLabel controlId="floatingInput" label="Notes" className="required-field">
            <Form.Control
              as="textarea"
              rows="3"
              value={inServiceForm.notes || ''}
              onChange={(e) => setInServiceForm({...inServiceForm, notes: e.target.value})}
            />
            </FloatingLabel>
          </Form.Group>
            </Col>
            <Col md={12} className="mb-3">
          <Form.Group>
            <Form.Label>Upload Exam PDF (optional)</Form.Label>
            <Form.Control
              type="file"
              accept="application/pdf"
              onChange={e => setPdfFile(e.target.files[0])}
            />
          </Form.Group>            
            </Col>
            <Col md={12} className="d-flex justify-content-end mt-3">
                      <Button type="submit" variant="primary" size="sm">
            {isEditingExam ? 'Update Exam Result' : 'Save Exam Result'}
          </Button>
            </Col>
          </Row>

        </Form>
      </Modal.Body>
    </Modal>
  );

  const handleBacktrackPromotion = async () => {
    if (!selectedResident) return;

    if (!window.confirm(`Are you sure you want to backtrack ${selectedResident.firstName} ${selectedResident.lastName} from PGY-${selectedResident.year} to PGY-${selectedResident.year - 1}? This action cannot be undone.`)) {
      return;
    }

    setBacktracking(true);
    try {
      console.log('🔄 Backtracking promotion for resident:', selectedResident.id);
      const headers = getAuthHeaders();
      const response = await axios.post(`${API_ENDPOINTS.PROMOTIONS}/residents/${selectedResident.id}/backtrack`, {}, { headers });
      console.log('✅ Backtrack response:', response.data);
      
      if (response.data.success) {
        alert(`Successfully backtracked ${response.data.resident.name} from PGY-${response.data.resident.oldPGY} to PGY-${response.data.resident.newPGY}. Removed promotion from ${response.data.resident.removedPromotionDate}`);
        setShowBacktrackModal(false);
        fetchData(); // Refresh data
      }
    } catch (err) {
      console.error('❌ Error backtracking promotion:', err);
      console.error('❌ Error response:', err.response?.data);
      alert('Error backtracking promotion: ' + (err.response?.data?.message || err.message));
    } finally {
      setBacktracking(false);
    }
  };

  const handlePromoteAll = async () => {
    if (!window.confirm('Are you sure you want to promote ALL eligible residents? This action cannot be undone.')) {
      return;
    }

    setPromoteAllLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.PROMOTIONS}/promote-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ promotionDate: moment().format('YYYY-MM-DD') })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Successfully promoted ${result.totalPromoted} residents. ${result.totalErrors} errors occurred.`);
        fetchData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.message}`);
      }
    } catch (err) {
      alert('Error promoting residents');
    } finally {
      setPromoteAllLoading(false);
    }
  };

  const getPGYBadgeColor = (pgy) => {
    const colors = {
      1: 'primary',
      2: 'success',
      3: 'warning',
      4: 'info',
      5: 'secondary',
      6: 'dark',
      7: 'danger'
    };
    return colors[pgy] || 'light';
  };

  const getGraduationStatus = (graduationYear) => {
    const currentYear = moment().year();
    if (graduationYear < currentYear) return { status: 'Graduated', color: 'success' };
    if (graduationYear === currentYear) return { status: 'Graduating', color: 'warning' };
    return { status: 'Active', color: 'primary' };
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up"></i>;
    return sortConfig.direction === 'asc' ? <i className="bi bi-arrow-up"></i> : <i className="bi bi-arrow-down"></i>;
  };

  // Filter and sort residents
  const getFilteredAndSortedResidents = () => {
    let filtered = residents.filter(resident => {
      const matchesSearch = searchTerm === '' || 
        `${resident.firstName} ${resident.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resident.email?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesPGY = pgyFilter === '' || (resident.year && resident.year.toString() === pgyFilter);
      
      return matchesSearch && matchesPGY;
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle special cases
        if (sortConfig.key === 'name') {
          aValue = `${a.firstName} ${a.lastName}`;
          bValue = `${b.firstName} ${b.lastName}`;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setPgyFilter('');
    setSortConfig({ key: null, direction: 'asc' });
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  // Pagination functions
  const getPaginatedResidents = () => {
    const filteredResidents = getFilteredAndSortedResidents();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredResidents.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const filteredResidents = getFilteredAndSortedResidents();
    return Math.ceil(filteredResidents.length / itemsPerPage);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pgyFilter, sortConfig]);

  // Debug PGY options
  useEffect(() => {
    console.log('Current pgyLevelOptions:', pgyLevelOptions);
  }, [pgyLevelOptions]);

  const createEvaluationAssignments = async () => {
    try {
      const headers = getAuthHeaders();
      
      // Use the selected academic year from the dropdown
      const academicYear = selectedAcademicYear;
      
      // Get promotion requirements for the selected academic year
      const response = await axios.get(`${API_ENDPOINTS.PROMOTIONS}/requirements/academic-year/${academicYear}`, { headers });
      
      if (response.data.success && response.data.requirements.length > 0) {
        const confirmed = window.confirm(
          `This will create evaluation assignments for the ${academicYear}-${academicYear + 1} academic year based on current promotion requirements.\n\n` +
          `Requirements found for:\n` +
          response.data.requirements.map(req => `- PGY-${req.pgyLevel}: ${req.requiredCaseLogs} case logs, ${req.requiresInServiceExam ? 'In-service exam required' : 'No in-service exam'}`).join('\n') +
          `\n\nContinue?`
        );
        
        if (confirmed) {
          // Only create assignments for PGY levels that have requirements
          for (const requirement of response.data.requirements) {
            console.log(`Creating evaluation assignments for PGY-${requirement.pgyLevel} in academic year ${academicYear}`);
            await axios.post(`${API_ENDPOINTS.PROMOTIONS}/create-evaluation-assignments`, {
              pgyLevel: requirement.pgyLevel,
              academicYear: requirement.academicYear
            }, { headers });
          }
          
          // Also create graduation evaluation assignments if PGY 5 requirements exist
          const pgy5Requirement = response.data.requirements.find(req => req.pgyLevel === 5);
          if (pgy5Requirement) {
            console.log(`Creating graduation evaluation assignments for academic year ${academicYear}`);
            await axios.post(`${API_ENDPOINTS.GRADUATIONS}/create-evaluation-assignments`, {
              academicYear: academicYear
            }, { headers });
          }
          
          alert(`Successfully created evaluation assignments for ${academicYear}-${academicYear + 1} academic year based on promotion requirements!`);
          fetchData(); // Refresh data
        }
      } else {
        alert(`No promotion requirements found for the ${academicYear}-${academicYear + 1} academic year. Please set requirements first.`);
      }
    } catch (err) {
      console.error('Error creating evaluation assignments:', err);
      setError(err.response?.data?.message || 'Failed to create evaluation assignments');
    }
  };



  // Hash navigation for Promotion Manager tabs
  useEffect(() => {
    function handleHashChange() {
      const hash = window.location.hash.replace('#', '');
      if (hash.startsWith('promotion-')) {
        const tab = hash.split('-')[1];
        if (["residents","requirements","exams","licenses","onboarding","training","vaccinations","scholarly"].includes(tab)) {
          setActiveTab(tab);
        }
      }
    }
    window.addEventListener('hashchange', handleHashChange);
    handleHashChange(); // run on mount
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    if (["residents","requirements","exams","licenses","onboarding","training","vaccinations","scholarly"].includes(activeTab)) {
      window.location.hash = `promotion-${activeTab}`;
    }
  }, [activeTab]);

  useEffect(() => {
    if (CHECKLIST_REVIEW_TAB_KEYS.includes(activeTab)) {
      fetchPromotionEvidence();
      fetchOnboardingDirectorReviewData();
      setChecklistNavOpen(true);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'requirements') {
      // Needed for the program-wide checklist stats card.
      fetchPromotionEvidence();
      fetchOnboardingDirectorReviewData();
    }
  }, [activeTab]);

  useEffect(() => {
    if (showRequirementsModal) {
      fetchOnboardingSources();
      fetchPromotionEvidence();
      fetchOnboardingDirectorReviewData();
      setPgySpecificDirty(false);
      setProgramWideDirty(false);
    }
  }, [showRequirementsModal]);

  useEffect(() => {
    fetchRequirements(selectedAcademicYear);
    if (activeTab === 'exams') {
      fetchInServiceExams(selectedAcademicYear);
    }
    if (activeTab === 'requirements' || CHECKLIST_REVIEW_TAB_KEYS.includes(activeTab) || showRequirementsModal) {
      fetchPromotionEvidence();
      fetchOnboardingDirectorReviewData();
    }
  }, [selectedAcademicYear]);

  if (loading) {
    return (
      <Container className="py-4">
        <div className="text-center">
          <Spinner animation="border" />
          <p className="mt-2">Loading residents...</p>
        </div>
      </Container>
    );
  }

  const checklistReviewNavItems = (linkClassName = '') => (
    <>
      <Nav.Item>
        <Nav.Link eventKey="licenses" className={linkClassName} active={activeTab === 'licenses'}>
          <i className="bi bi-person-vcard me-2"></i>Licenses
        </Nav.Link>
      </Nav.Item>
      <Nav.Item>
        <Nav.Link eventKey="onboarding" className={linkClassName} active={activeTab === 'onboarding'}>
          <i className="bi bi-clipboard-check me-2"></i>Onboarding
        </Nav.Link>
      </Nav.Item>
      <Nav.Item>
        <Nav.Link eventKey="training" className={linkClassName} active={activeTab === 'training'}>
          <i className="bi bi-easel2 me-2"></i>Training
        </Nav.Link>
      </Nav.Item>
      <Nav.Item>
        <Nav.Link eventKey="vaccinations" className={linkClassName} active={activeTab === 'vaccinations'}>
          <div className="d-flex align-items-center">
          <FaSyringe size={14} />
          <span className="ms-2">Vaccinations</span>  
          </div>
        </Nav.Link>
      </Nav.Item>
      <Nav.Item>
        <Nav.Link eventKey="scholarly" className={linkClassName} active={activeTab === 'scholarly'}>
          <i className="bi bi-mortarboard me-2"></i>Scholarly
        </Nav.Link>
      </Nav.Item>
    </>
  );

  const renderChecklistNavGroup = (submenuId, subLinkClassName, parentButtonClassName = 'py-2') => (
    <>
      <Nav.Item className="mx-1">
        <button
          type="button"
          className={`nav-link-checklist w-100 text-start border-0 bg-transparent d-flex align-items-center justify-content-between ${parentButtonClassName} ${CHECKLIST_REVIEW_TAB_KEYS.includes(activeTab) ? 'fw-semibold' : ''}`}
          onClick={() => setChecklistNavOpen((open) => !open)}
          aria-expanded={checklistNavOpen}
          aria-controls={submenuId}
        >
          <span>Checklist items</span>
          <i className={`bi ${checklistNavOpen ? 'bi-chevron-up' : 'bi-chevron-down'} small opacity-75`} aria-hidden />
        </button>
      </Nav.Item>
      <Collapse in={checklistNavOpen}>
        <div id={submenuId} className="ps-1 mb-1" style={{ borderColor: 'var(--bs-border-color, #dee2e6)' }}>
          {checklistReviewNavItems(subLinkClassName)}
        </div>
      </Collapse>
    </>
  );

  const selectedYearRequirementRows = (requirements || []).filter(
    (r) => parseInt(r.academicYear, 10) === parseInt(selectedAcademicYear, 10)
  );
  const selectedYearRequirementSource = selectedYearRequirementRows[0] || null;
  const selectedYearRequiredItems = {
    licensesCertifications: new Set(
      ((selectedYearRequirementSource?.licensesCertifications || []).filter((it) => it && it.required).map((it) => it.name))
    ),
    trainingCertifications: new Set(
      ((selectedYearRequirementSource?.trainingCertifications || []).filter((it) => it && it.required).map((it) => it.name))
    ),
    vaccinations: new Set(
      ((selectedYearRequirementSource?.vaccinations || []).filter((it) => it && it.required).map((it) => it.name))
    ),
    scholarlyActivity: new Set(
      ((selectedYearRequirementSource?.scholarlyActivity || []).filter((it) => it && it.required).map((it) => it.name))
    )
  };
  const selectedYearVaccinationCardPgyLevels = selectedYearRequirementRows
    .filter((r) => r.vaccinationCardRequired)
    .map((r) => parseInt(r.pgyLevel, 10))
    .filter((n) => Number.isFinite(n));

  const evidenceReviewRowsLicenses = promotionEvidence.filter(
    (item) => item.category === 'licensesCertifications' && selectedYearRequiredItems.licensesCertifications.has(item.itemName)
  );
  const evidenceReviewRowsOnboarding = buildOnboardingReviewMergedRows({
    promotionEvidence,
    submissions: onboardingDirectorSubmissions,
    assignments: onboardingDirectorAssignments,
    residents,
    requirements,
    selectedAcademicYear
  });
  const evidenceReviewRowsTraining = promotionEvidence.filter(
    (item) => item.category === 'trainingCertifications' && selectedYearRequiredItems.trainingCertifications.has(item.itemName)
  );
  const vaccinationChecklistRows = promotionEvidence.filter(
    (item) => item.category === 'vaccinations' && selectedYearRequiredItems.vaccinations.has(item.itemName)
  );
  const requiredStructuredCardRows = promotionEvidence.filter((item) => {
    if (item.category !== 'vaccinationCard') return false;
    const residentYear = parseInt(item?.resident?.year, 10);
    return selectedYearVaccinationCardPgyLevels.includes(residentYear);
  });
  const vaccinationEvidenceIdsByResident = new Map();
  vaccinationChecklistRows.forEach((item) => {
    if (item.status !== 'approved') return;
    const vaccineId = mapPromotionVaccineNameToCardVaccineId(item.itemName);
    if (!vaccineId) return;
    const residentId = item.residentId ?? item.resident?.id;
    if (residentId == null) return;
    const key = String(residentId);
    if (!vaccinationEvidenceIdsByResident.has(key)) {
      vaccinationEvidenceIdsByResident.set(key, new Set());
    }
    vaccinationEvidenceIdsByResident.get(key).add(Number(item.id));
  });
  const latestStructuredCardByResident = new Map();
  promotionEvidence.forEach((item) => {
    if (item.category !== 'vaccinationCard' || item.itemName !== VACCINATION_CARD_ITEM_NAME) return;
    const residentId = item.residentId ?? item.resident?.id;
    if (residentId == null) return;
    const key = String(residentId);
    const existing = latestStructuredCardByResident.get(key);
    const itemTime = new Date(item.updatedAt || item.reviewedAt || 0).getTime();
    const existingTime = existing ? new Date(existing.updatedAt || existing.reviewedAt || 0).getTime() : -1;
    if (!existing || itemTime >= existingTime) {
      latestStructuredCardByResident.set(key, item);
    }
  });
  const extraStructuredCardRows = [];
  latestStructuredCardByResident.forEach((cardRow, residentKey) => {
    const residentEvidenceIds = vaccinationEvidenceIdsByResident.get(residentKey);
    if (!residentEvidenceIds || residentEvidenceIds.size === 0) return;
    const residentYear = parseInt(cardRow?.resident?.year, 10);
    const isAlreadyInRequiredGroup = selectedYearVaccinationCardPgyLevels.includes(residentYear);
    if (isAlreadyInRequiredGroup) return;
    if (structuredCardHasDoseForEvidenceIds(cardRow.details, residentEvidenceIds)) {
      extraStructuredCardRows.push(cardRow);
    }
  });
  const evidenceReviewRowsVaccinations = [
    ...vaccinationChecklistRows,
    ...requiredStructuredCardRows,
    ...extraStructuredCardRows
  ].filter(
    (item, idx, arr) => idx === arr.findIndex((x) => String(x.id) === String(item.id))
  );
  const evidenceReviewRowsScholarly = promotionEvidence.filter(
    (item) => item.category === 'scholarlyActivity' && selectedYearRequiredItems.scholarlyActivity.has(item.itemName)
  );

  return (
    <div className="mt-2">
             {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      

      {/* Tabs */}
      <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
        {/* Mobile menu */}
        <div className="d-md-none mb-3">
          <Accordion defaultActiveKey={null}>
            <Card className="bg-white shadow-sm border-0">
              <Accordion.Item eventKey="0" className="border-0">
                <Accordion.Header>Menu</Accordion.Header>
                <Accordion.Body className="p-0">
                  <Nav variant="pills" className="flex-column">
                    <Nav.Item>
                      <Nav.Link eventKey="residents" active={activeTab==='residents'} onClick={() => setActiveTab('residents')} className="px-3 py-2">Residents</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="requirements" active={activeTab==='requirements'} onClick={() => setActiveTab('requirements')} className="px-3 py-2">Requirements</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="exams" active={activeTab==='exams'} onClick={() => setActiveTab('exams')} className="px-3 py-2">Exam Records</Nav.Link>
                    </Nav.Item>
                    {renderChecklistNavGroup('promotion-checklist-subnav-mobile', 'px-3 py-2', 'px-3 py-2')}
                  </Nav>
                </Accordion.Body>
              </Accordion.Item>
            </Card>
          </Accordion>
        </div>
        <Row>
          {/* Sidebar menu hidden on small screens */}
          <Col lg={2} md={3} className="d-none d-md-block mb-3">
            <Card className="bg-white shadow-sm border-0 p-0 py-2 custom-sticky-top" style={{ top: '5rem' }}>
              <Nav variant="pills" className="flex-column">
                <Nav.Item>
                  <Nav.Link className="disabled fw-semibold">Menu</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="residents">Residents</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="requirements">Requirements</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="exams">Exam Records</Nav.Link>
                </Nav.Item>
                {renderChecklistNavGroup('promotion-checklist-subnav-desktop', 'py-1 small', 'py-2')}
              </Nav>
            </Card>
          </Col>

          <Col lg={10} md={9} sm={12}>        
            <Tab.Content>
              {/* Residents Tab */}
              <Tab.Pane eventKey="residents">
      {/* Search and Filter Controls */}
      <Card className="mb-4 shadow-sm border-0">
        <Card.Header className="border-0 px-4 py-3 d-flex flex-column flex-md-row justify-content-between">
          <div className="d-flex flex-column justify-content-start align-items-start">
            <h6 className="mb-0 fw-semibold">Residents</h6>
            <p className="text-muted small mb-0">showing {getPaginatedResidents().length} of {residents.length} residents</p>
          </div>
          <div className="d-flex flex-row align-items-center justify-content-start gap-2">
            {getFilteredAndSortedResidents().length > 0 && (
             <div className="d-flex align-items-center gap-3">
                <Form.Select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                  size="sm"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                </Form.Select>
              </div>
            )}
            {(searchTerm || pgyFilter) && (
                <Button
                  variant="light-solid"
                  size="sm"
                  onClick={clearFilters}
                  className="d-flex align-items-center"
                >
                  <i className="bi bi-x-lg"></i> <span className="d-none d-md-inline-block ms-1">clear</span>
                </Button>
              )}
            <Form.Group>
                <Form.Select
                  value={pgyFilter}
                  onChange={(e) => setPgyFilter(e.target.value)}
                  size="sm"
                  style={{ width: '110px' }}
                >
                  <option value="">All</option>
                  {(pgyLevelOptions.length > 0 ? pgyLevelOptions : Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `PGY-${i + 1}` }))).map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            <Button 
                  variant="primary" 
                  size="sm"
                  onClick={handlePromoteAll}
                  disabled={promoteAllLoading}
                  className="no-wrap"
                >
                  <i className="bi bi-arrow-up d-block d-md-none"></i>
                  <span className="d-none d-md-block">{promoteAllLoading ? <Spinner animation="border" size="sm" /> : 'Promote All'}</span>
                </Button>
            </div>
        </Card.Header>
        <Card.Body className="p-0">
      {/* Residents Table */}
          <Table responsive hover>
            <thead>
              <tr className="custom-header-bg">
                <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('name')}
                  className="px-4"
                >
                  Name {getSortIcon('name')}
                </th>
                <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('year')}
                >
                  PGY Level { getSortIcon('year')}
                </th>
                <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('academicYearStart')}
                >
                  Academic Year Start {getSortIcon('academicYearStart')}
                </th>
                <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('graduationYear')}
                >
                  Graduation Year {getSortIcon('graduationYear')}
                </th>
                <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('lastPromotionDate')}
                >
                  Last Promotion {getSortIcon('lastPromotionDate')}
                </th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedResidents().map(resident => {
                const graduationStatus = getGraduationStatus(resident.graduationYear);
                return (
                  <tr 
                    key={resident.id} 
                    style={{ cursor: 'pointer' }}
                    onClick={() => checkEligibility(resident.id)}

                  >
                    <td className="px-4"><div className="d-flex align-items-center gap-2"><UserAvatar user={resident} size={32} alt="" />{resident.firstName} {resident.lastName}</div></td>
                    <td>
                      <Badge bg={getPGYBadgeColor(resident.year)}>
                        PGY-{resident.year}
                      </Badge>
                    </td>
                    <td>
                      {resident.academicYearStart ? 
                        moment(resident.academicYearStart).format('MMM D, YYYY') : 
                        'Not set'
                      }
                    </td>
                    <td>{resident.graduationYear || 'Not set'}</td>
                    <td>
                      {resident.lastPromotionDate ? 
                        moment(resident.lastPromotionDate).format('MMM D, YYYY') : 
                        'Not set'
                      }
                    </td>
                    <td>
                      <Badge className="rounded-2" bg={graduationStatus.color}>
                        {graduationStatus.status}
                      </Badge>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <Dropdown>
                        <Dropdown.Toggle variant="light-lite" size="sm" id={`dropdown-actions-${resident.id}`} className="no-arrow">
                          <i className="bi bi-three-dots-vertical"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => {
                              setSelectedResident(resident);
                              setShowPromoteModal(true);
                            }}
                          >
                            <i className="bi bi-arrow-up"></i> Promote
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setSelectedResident(resident);
                              setShowBacktrackModal(true);
                            }}
                          >
                            <i className="bi bi-arrow-down"></i> Backtrack
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setSelectedResident(resident);
                              setShowHistoryModal(true);
                            }}
                          >
                            <i className="bi bi-clock-history"></i> History
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Pagination Controls */}
      {getFilteredAndSortedResidents().length > 0 && (
        <Row className="mt-3">
          <Col>
            <div className="d-flex justify-content-end align-items-center mb-3"> 
              {getTotalPages() > 1 && (
                <Pagination size="sm" className="mb-0">
                  {/*<Pagination.First 
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                  />*/}
                  <Pagination.Prev 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  />
                  
                  {/* Show page numbers */}
                  {Array.from({ length: Math.min(5, getTotalPages()) }, (_, i) => {
                    let pageNumber;
                    if (getTotalPages() <= 5) {
                      pageNumber = i + 1;
                    } else if (currentPage <= 3) {
                      pageNumber = i + 1;
                    } else if (currentPage >= getTotalPages() - 2) {
                      pageNumber = getTotalPages() - 4 + i;
                    } else {
                      pageNumber = currentPage - 2 + i;
                    }
                    
                    return (
                      <Pagination.Item
                        key={pageNumber}
                        active={pageNumber === currentPage}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Pagination.Item>
                    );
                  })}
                  
                  <Pagination.Next 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === getTotalPages()}
                  />
                    {/*<Pagination.Last 
                    onClick={() => handlePageChange(getTotalPages())}
                    disabled={currentPage === getTotalPages()}
                  />*/}
                </Pagination>
              )}
            </div>
          </Col>
        </Row>
      )}
              </Tab.Pane>

              {/* Requirements Tab */}
              <Tab.Pane eventKey="requirements">
                <Card className="shadow-sm border-0 mb-3">
                    <Card.Header className="px-4 py-3 border-0 d-flex flex-md-row flex-column justify-content-between">
                        <div className="d-flex mb-2 mb-md-0 flex-column">
                          <h6 className="mb-0 fw-semibold">PGY-level specific Checklist</h6>
                          <p className="text-muted small mb-0">showing {requirements.length} promotion requirements</p>
                        </div>
                        <div className="d-flex flex-row align-items-center gap-2">
                        <Form.Group>
                          <Form.Select
                            value={selectedAcademicYear}
                            onChange={(e) => {
                              setSelectedAcademicYear(parseInt(e.target.value));
                              fetchRequirements(parseInt(e.target.value));
                            }}
                            size="sm"
                          >
                            <option value={currentYear}>{currentYear}-{currentYear + 1}</option>
                            <option value={currentYear + 1}>{currentYear + 1}-{currentYear + 2}</option>
                            <option value={currentYear - 1}>{currentYear - 1}-{currentYear}</option>
                          </Form.Select>
                        </Form.Group>
                        <Button 
                          variant="light-solid" 
                          size="sm" 
                          className="d-flex align-items-cente text-nowrap"
                          onClick={createEvaluationAssignments}
                          title="Create evaluation assignments for existing residents based on current requirements"
                        >
                            <i className="bi bi-list-task"></i> <span className="d-none d-md-inline ms-1">Create Evaluations</span>
                        </Button>

                        
                          
                          <Dropdown as={ButtonGroup} align="end">
                            <Button
                            variant="primary"
                            size="sm"
                            onClick={openPgySpecificRequirementsModal}
                            className="d-flex align-items-center text-nowrap"
                          >
                            <i className="bi bi-plus-lg"></i>{' '}
                            <span className="d-none d-md-inline ms-1">Requirements</span>
                          </Button>
                            <Dropdown.Toggle
                              variant="primary"
                              size="sm"
                              id="copy-requirements-dropdown"
                              className="px-2 d-flex align-items-center justify-content-center"
                            >

                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => openCopyRequirementsModal({ copyPgySpecific: true, copyProgramWideEvidence: false })}>
                                Copy PGY-level specific reqs
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => openCopyRequirementsModal({ copyPgySpecific: false, copyProgramWideEvidence: true })}>
                                Copy program-wide checklist
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => openCopyRequirementsModal({ copyPgySpecific: true, copyProgramWideEvidence: true })}>
                                Copy both
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0">

                    {requirements.length > 0 ? (
                      <Table responsive>
                        <thead>
                          <tr className="custom-header-bg">
                            <th className="px-4">PGY Level</th>
                            <th>Case Logs Required</th>
                            <th>Biannual Evaluation</th>
                            <th>In-Service Exam</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {requirements.map((req, index) => (
                            <tr key={index}>
                              <td className="px-4">
                                <Badge bg={getPGYBadgeColor(req.pgyLevel)}>
                                  PGY-{req.pgyLevel}
                                </Badge>
                              </td>
                              <td>{req.requiredCaseLogs}</td>
                              <td>
                                {req.evaluationTemplate && (
                                  <div>
                                    <div>{req.evaluationTemplate.title}</div>
                                  </div>
                                )}
                              </td>
                              <td>
                                {req.requiresInServiceExam ? (
                                  <Badge className="rounded-2" bg={req.inServiceExamPassRequired ? 'danger' : 'warning'}>
                                    {req.inServiceExamPassRequired ? 'Pass Required' : 'Required'}
                                  </Badge>
                                ) : (
                                  <Badge className="rounded-2" bg="secondary">
                                    Not Required
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <Dropdown align="end">
                                  <Dropdown.Toggle variant="light-lite" size="sm" className="no-arrow">
                                    <i className="bi bi-three-dots-vertical"></i>
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => {
                                      const vCardLevels = requirements
                                        .filter((r) => parseInt(r.academicYear, 10) === parseInt(req.academicYear, 10) && r.vaccinationCardRequired)
                                        .map((r) => r.pgyLevel)
                                        .sort((a, b) => a - b);
                                      const nextForm = {
                                        pgyLevel: req.pgyLevel,
                                        academicYear: req.academicYear,
                                        requiredCaseLogs: req.requiredCaseLogs,
                                        evaluationTemplateId: req.evaluationTemplateId,
                                        requiresInServiceExam: req.requiresInServiceExam,
                                        inServiceExamPassRequired: req.inServiceExamPassRequired,
                                        licensesCertifications: req.licensesCertifications || [],
                                        onboardingRequirements: req.onboardingRequirements || [],
                                        trainingCertifications: req.trainingCertifications || [],
                                        vaccinations: req.vaccinations || [],
                                        scholarlyActivity: req.scholarlyActivity || [],
                                        vaccinationCardPgyLevels: vCardLevels
                                      };
                                      setRequirementForm(nextForm);
                                      setCategoryDrafts(categoryDraftsForPrefilledItemsWithVaccinationCard({
                                        licensesCertifications: nextForm.licensesCertifications,
                                        onboardingRequirements: nextForm.onboardingRequirements,
                                        trainingCertifications: nextForm.trainingCertifications,
                                        vaccinations: nextForm.vaccinations,
                                        scholarlyActivity: nextForm.scholarlyActivity
                                      }, vCardLevels));
                                      setIsEditingRequirement(true);
                                      setRequirementsModalNav('pgy-specific');
                                      setShowRequirementsModal(true);
                                    }}>
                                      <i className="bi bi-pencil"></i> Edit
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => {
                                      setConfirmMessage(`Are you sure you want to delete the promotion requirements for PGY-${req.pgyLevel} in academic year ${req.academicYear}-${req.academicYear + 1}? This action cannot be undone.`);
                                      setConfirmAction(() => () => deleteRequirement(req.pgyLevel, req.academicYear));
                                      setShowConfirmModal(true);
                                    }}>
                                      <i className="bi bi-trash-fill"></i> Delete
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info">
                        No promotion requirements set for {selectedAcademicYear}-{selectedAcademicYear + 1}. Click "Add Requirements" to get started.
                      </Alert>
                    )}
                    </Card.Body>
                </Card>
                {/* Program-wide Checklist summary */}
                <Card className="mb-3 shadow-sm border-0">
                      <Card.Header className="px-4 py-3 border-0 d-flex align-items-center justify-content-between">
                        <div className="d-flex flex-column">
                          <h6 className="mb-0 fw-semibold">Program-wide Checklist</h6>
                          <p className="text-muted small mb-0">What this program requires for every PGY level</p>
                        </div>
                        <Button variant="light-solid" size="sm" onClick={openAddRequirementsModal} className="d-flex align-items-center text-nowrap">
                          <i className="bi bi-pencil"></i> <span className="d-none d-md-inline ms-1">Edit</span>
                        </Button>
                      </Card.Header>
                      <Card.Body className="px-4 py-3">
                        {requirements.length === 0 ? (
                          <div className="text-muted small">No program-wide checklist has been set yet for this academic year.</div>
                        ) : (
                          (() => {
                            const source = requirements[0];

                            const startOfDay = (d) => {
                              const x = new Date(d);
                              x.setHours(0, 0, 0, 0);
                              return x;
                            };

                            const today = startOfDay(new Date());

                            const getDefaultRecurrenceType = (categoryKey) => {
                              if (categoryKey === 'licensesCertifications') return 'until_expiration_until_graduation';
                              if (categoryKey === 'trainingCertifications' || categoryKey === 'vaccinations') return 'yearly_until_graduation';
                              return 'until_complete';
                            };

                            const isApprovedEvidenceValid = (categoryKey, requiredItem, evidence) => {
                              const recurrenceType = requiredItem?.recurrenceType || getDefaultRecurrenceType(categoryKey);
                              if (recurrenceType === 'until_complete') return true;
                              const effectiveRaw = evidence?.reviewedAt || evidence?.updatedAt;
                              const effective = effectiveRaw ? new Date(effectiveRaw) : null;
                              if (!effective || Number.isNaN(effective.getTime())) return false;
                              const effectiveSod = startOfDay(effective);

                              if (recurrenceType === 'yearly_until_graduation') {
                                const due = new Date(effectiveSod);
                                due.setFullYear(due.getFullYear() + 1);
                                return startOfDay(due).getTime() >= today.getTime();
                              }

                              if (recurrenceType === 'until_expiration_until_graduation') {
                                if (!evidence?.expirationDate) return false;
                                const exp = startOfDay(new Date(evidence.expirationDate));
                                return exp.getTime() >= today.getTime();
                              }

                              // Back-compat: treat licenses expiration as trigger when configured that way.
                              if (categoryKey === 'licensesCertifications' && evidence?.expirationDate) {
                                const exp = startOfDay(new Date(evidence.expirationDate));
                                return exp.getTime() >= today.getTime();
                              }

                              return true;
                            };

                            const categories = [
                              { key: 'licensesCertifications', label: 'Licenses & Certifications' },
                              { key: 'onboardingRequirements', label: 'Onboarding Requirements' },
                              { key: 'trainingCertifications', label: 'Training Certifications' },
                              { key: 'vaccinations', label: 'Vaccinations' },
                              { key: 'scholarlyActivity', label: 'Scholarly Activity' }
                            ];

                            const requiredItemsByCategory = categories.reduce((acc, c) => {
                              const items = Array.isArray(source?.[c.key]) ? source[c.key].filter((it) => it && it.required) : [];
                              acc[c.key] = items;
                              return acc;
                            }, {});

                            const residentsById = new Map((residents || []).map((r) => [String(r.id), r]));
                            const vCardLevels = (requirements || [])
                              .filter((r) => parseInt(r.academicYear, 10) === parseInt(selectedAcademicYear, 10) && r.vaccinationCardRequired)
                              .map((r) => parseInt(r.pgyLevel, 10))
                              .filter((n) => Number.isFinite(n))
                              .sort((a, b) => a - b);
                            const vaccinationCardEligibleResidentIds = new Set(
                              (residents || [])
                                .filter((res) => vCardLevels.includes(parseInt(res?.year, 10)))
                                .map((res) => String(res.id))
                            );
                            const vaccinationCardCompletion = getEvidenceCompletionCounts(
                              promotionEvidence,
                              'vaccinationCard',
                              VACCINATION_CARD_ITEM_NAME,
                              vaccinationCardEligibleResidentIds.size > 0 ? vaccinationCardEligibleResidentIds : null
                            );

                            // Build a latest-approved-or-not index for required items only:
                            // key: category::itemName::residentId -> evidence
                            const neededItemKeys = new Set();
                            Object.entries(requiredItemsByCategory).forEach(([categoryKey, items]) => {
                              (items || []).forEach((it) => neededItemKeys.add(`${categoryKey}::${it.name}`));
                            });

                            const latestEvidenceIndex = new Map();
                            promotionEvidence.forEach((ev) => {
                              const itemKey = `${ev.category}::${ev.itemName}`;
                              if (!neededItemKeys.has(itemKey)) return;
                              const rid = ev.residentId;
                              if (!rid) return;
                              const idxKey = `${ev.category}::${ev.itemName}::${rid}`;
                              const existing = latestEvidenceIndex.get(idxKey);
                              if (!existing || new Date(ev.updatedAt || ev.reviewedAt || 0) > new Date(existing.updatedAt || existing.reviewedAt || 0)) {
                                latestEvidenceIndex.set(idxKey, ev);
                              }
                            });

                            const getLatestEvidence = (categoryKey, itemName, residentId) => {
                              return latestEvidenceIndex.get(`${categoryKey}::${itemName}::${residentId}`) || null;
                            };

                            return (
                              <div className="d-flex flex-column gap-2">
                                {categories.map((c) => {
                                  const items = requiredItemsByCategory[c.key] || [];
                                  const hasStructuredCard = c.key === 'vaccinations' && vCardLevels.length > 0;

                                  if (items.length === 0 && !hasStructuredCard) {
                                    return (
                                      <div key={c.key} className="d-flex justify-content-between align-items-start gap-3">
                                        <div>
                                          <div className="fw-semibold small">{c.label}</div>
                                          <div className="text-muted small">—</div>
                                        </div>
                                      </div>
                                    );
                                  }

                                  const renderItemProgressRow = (item, itemKey) => {
                                    const eligibleResidentIds = (residents || [])
                                      .filter((res) => {
                                        if (c.key !== 'onboardingRequirements') return true;
                                        const requiredLvls = Array.isArray(item.requiredPgyLevels)
                                          ? item.requiredPgyLevels.map((n) => parseInt(n, 10)).filter((n) => Number.isFinite(n))
                                          : [];
                                        if (!requiredLvls.length) return true;
                                        return requiredLvls.includes(parseInt(res?.year, 10));
                                      })
                                      .map((res) => res.id);

                                    let completedResidents = 0;
                                    let renewalResidents = new Set();
                                    let activeEvidenceCount = 0;
                                    let expiredEvidenceCount = 0;

                                    eligibleResidentIds.forEach((residentId) => {
                                      const evidence = getLatestEvidence(c.key, item.name, residentId);
                                      const approved = evidence?.status === 'approved';
                                      const valid = approved ? isApprovedEvidenceValid(c.key, item, evidence) : false;

                                      if (approved && valid) completedResidents += 1;

                                      if (c.key === 'licensesCertifications' && approved) {
                                        if (valid) activeEvidenceCount += 1;
                                        else expiredEvidenceCount += 1;
                                      }

                                      if (
                                        (c.key === 'trainingCertifications' || c.key === 'vaccinations') &&
                                        (item.recurrenceType || getDefaultRecurrenceType(c.key)) === 'yearly_until_graduation' &&
                                        approved &&
                                        !valid
                                      ) {
                                        renewalResidents.add(residentId);
                                      }
                                    });

                                    const totalResidents = eligibleResidentIds.length;
                                    const pendingResidents = Math.max(0, totalResidents - completedResidents);
                                    const completedPct = totalResidents > 0 ? Math.round((completedResidents / totalResidents) * 100) : 0;
                                    const pendingPct = Math.max(0, 100 - completedPct);
                                    const requiredLevelsText = c.key === 'onboardingRequirements' && Array.isArray(item.requiredPgyLevels) && item.requiredPgyLevels.length > 0
                                      ? `PGY ${item.requiredPgyLevels.join(', ')}`
                                      : null;

                                    return (
                                      <div key={itemKey} className="d-flex justify-content-between align-items-start gap-3">
                                        <div className="flex-grow-1">
                                          <div className="small">{item.name}</div>
                                          {requiredLevelsText ? <div className="text-muted small">{requiredLevelsText}</div> : null}
                                        </div>
                                        <div className="d-flex justify-content-end gap-4 flex-wrap">
                                          <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
                                            {c.key === 'licensesCertifications' ? (
                                              <>
                                                <Badge bg="success" pill>
                                                  <b>{activeEvidenceCount}</b> active
                                                </Badge>
                                                <Badge bg="danger" pill>
                                                  <b>{expiredEvidenceCount}</b> expired
                                                </Badge>
                                              </>
                                            ) : null}
                                            {(c.key === 'trainingCertifications' || c.key === 'vaccinations') ? (
                                              <Badge bg="warning" pill>
                                                <b>{renewalResidents.size}</b> need renewal
                                              </Badge>
                                            ) : null}
                                          </div>
                                          <div className="text-end">
                                            <div className="small">
                                              <b>{completedResidents}</b>/{totalResidents} complete{' '}
                                              <span className="text-muted">· {pendingResidents} pending</span>
                                            </div>
                                            <div className="progress mt-2" style={{ height: '8px', minWidth: 180 }}>
                                              <div className="progress-bar bg-success-solid" style={{ width: `${completedPct}%` }} />
                                              <div className="progress-bar bg-secondary" style={{ width: `${pendingPct}%` }} />
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  };

                                  const structuredTotalResidents = vaccinationCardEligibleResidentIds.size;
                                  const structuredCompletedResidents = vaccinationCardCompletion.approved;
                                  const structuredPendingResidents = Math.max(0, structuredTotalResidents - structuredCompletedResidents);
                                  const structuredCompletedPct = structuredTotalResidents > 0 ? Math.round((structuredCompletedResidents / structuredTotalResidents) * 100) : 0;
                                  const structuredPendingPct = Math.max(0, 100 - structuredCompletedPct);

                                  return (
                                    <div key={c.key} className="border-bottom pb-3 mb-3">
                                      <div className="fw-semibold small mb-1">{c.label}</div>
                                      <div className="d-flex flex-column gap-2">
                                        {items.map((item, idx) => renderItemProgressRow(item, `${c.key}-${idx}-${item.name}`))}
                                        {hasStructuredCard ? (
                                          <div className="d-flex justify-content-between align-items-start gap-3">
                                            <div className="flex-grow-1">
                                              <div className="small">Structured vaccination card</div>
                                              <div className="text-muted small">PGY {vCardLevels.join(', ')}</div>
                                            </div>
                                            <div className="text-end">
                                              <div className="small">
                                                <b>{structuredCompletedResidents}</b>/{structuredTotalResidents} complete{' '}
                                                <span className="text-muted">· {structuredPendingResidents} pending</span>
                                              </div>
                                              <div className="progress mt-2" style={{ height: '8px', minWidth: 180 }}>
                                                <div className="progress-bar bg-success-solid" style={{ width: `${structuredCompletedPct}%` }} />
                                                <div className="progress-bar bg-secondary" style={{ width: `${structuredPendingPct}%` }} />
                                              </div>
                                            </div>
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()
                        )}
                      </Card.Body>
                </Card>
              </Tab.Pane>

              {/* In-Service Exams Tab */}
              <Tab.Pane eventKey="exams">
                <Card className="shadow-sm border-0">
                    <Card.Header className="px-4 py-3 border-0 d-flex flex-row align-items-center justify-content-between">
                        <div className="d-flex flex-column">
                          <h6 className="mb-0 fw-semibold">In-Service Exam Results</h6>
                          <p className="text-muted small mb-0">showing {inServiceExams.length} in-service exam results</p>
                        </div>
                        <div className="d-flex flex-row align-items-center gap-2">
                        <Form.Group>   
                          <Form.Select
                            value={selectedAcademicYear}
                            size="sm"
                            onChange={(e) => {
                              setSelectedAcademicYear(parseInt(e.target.value));
                              fetchInServiceExams(parseInt(e.target.value));
                            }}
                          >
                            <option value={currentYear}>{currentYear}-{currentYear + 1}</option>
                            <option value={currentYear + 1}>{currentYear + 1}-{currentYear + 2}</option>
                            <option value={currentYear - 1}>{currentYear - 1}-{currentYear}</option>
                          </Form.Select>
                        </Form.Group>
                        <Button 
                      variant="light-solid" 
                      size="sm"
                      className="d-flex align-items-center"
                      onClick={() => {
                        resetInServiceForm();
                        setShowInServiceModal(true);
                      }}
                    >
                      <i className="bi bi-plus"></i> <span className="d-none d-md-block ms-1">Record Exam</span>
                    </Button>
                    </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                    {inServiceExams.length > 0 ? (
                      <Table responsive >
                        <thead>
                          <tr className="custom-header-bg">
                            <th className="px-4">Resident</th>
                            <th>PGY Level</th>
                            <th>Exam Date</th>
                            <th>Result</th>
                            <th>Score</th>
                            <th>Administrator</th>
                            <th>Notes</th>
                            <th>PDF</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inServiceExams.map((exam, index) => {
                                                         const pdfUrl = exam.pdfFile ? 
                                                           (exam.pdfFile.startsWith('http') ? exam.pdfFile : 
                                                           exam.pdfFile.startsWith('/uploads/') ? `${API_ENDPOINTS.PROFILE_PICS}${exam.pdfFile}` : 
                                                           `${API_ENDPOINTS.UPLOADS}/${exam.pdfFile}`) : null;
                            return (
                              <tr key={index}>
                                <td className="px-4">{exam.resident.firstName} {exam.resident.lastName}</td>
                                <td>
                                  <Badge bg={getPGYBadgeColor(exam.pgyLevel)}>
                                    PGY-{exam.pgyLevel}
                                  </Badge>
                                </td>
                                <td>{moment(exam.examDate).format('MMM D, YYYY')}</td>
                                <td>
                                  <Badge className="rounded-2" bg={exam.result === 'pass' ? 'success' : 'danger'}>
                                    {exam.result.toUpperCase()}
                                  </Badge>
                                </td>
                                <td>{exam.score || 'N/A'}</td>
                                <td>{exam.administrator.firstName} {exam.administrator.lastName}</td>
                                <td>{exam.notes || 'N/A'}</td>
                                <td>
                                  {pdfUrl ? (
                                    <>
                                      <Button 
                                        variant="outline-primary" 
                                        size="sm" 
                                        onClick={async () => {
                                          try {
                                            const token = localStorage.getItem('token');
                                            const response = await fetch(pdfUrl, {
                                              headers: {
                                                'Authorization': `Bearer ${token}`,
                                                'Content-Type': 'application/pdf'
                                              }
                                            });
                                            
                                            if (!response.ok) {
                                              throw new Error(`HTTP error! status: ${response.status}`);
                                            }
                                            
                                            const blob = await response.blob();
                                            const url = window.URL.createObjectURL(blob);
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.download = `exam-${exam.examDate || 'document'}.pdf`;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                            window.URL.revokeObjectURL(url);
                                          } catch (error) {
                                            console.error('Error downloading PDF:', error);
                                            alert('Failed to download PDF. Please try again.');
                                          }
                                        }}
                                        title="Download PDF" 
                                        style={{ marginLeft: 8 }}
                                      >
                                        <i className="bi bi-download"></i> PDF
                                      </Button>
                                    </>
                                  ) : 'N/A'}
                                </td>
                                <td>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => editInServiceExam(exam)}
                                    title="Edit Exam"
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => {
                                      setConfirmMessage('Are you sure you want to delete this in-service exam record? This action cannot be undone.');
                                      setConfirmAction(() => () => deleteInServiceExam(exam.id));
                                      setShowConfirmModal(true);
                                    }}
                                    title="Delete Exam"
                                  >
                                    <i className="bi bi-trash-fill"></i>
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info">
                        No in-service exam results recorded for {selectedAcademicYear}-{selectedAcademicYear + 1}. Click "Record Exam" to add results.
                      </Alert>
                    )}
                    </Card.Body>
                </Card>
              </Tab.Pane>
              <PromotionEvidenceReviewTabPanes
                evidenceReviewRowsLicenses={evidenceReviewRowsLicenses}
                evidenceReviewRowsOnboarding={evidenceReviewRowsOnboarding}
                evidenceReviewRowsTraining={evidenceReviewRowsTraining}
                evidenceReviewRowsVaccinations={evidenceReviewRowsVaccinations}
                evidenceReviewRowsScholarly={evidenceReviewRowsScholarly}
                selectedAcademicYear={selectedAcademicYear}
                setSelectedAcademicYear={setSelectedAcademicYear}
                vaccinationSubmissionsExpandedIds={vaccinationSubmissionsExpandedIds}
                toggleVaccinationSubmissionsPanel={toggleVaccinationSubmissionsPanel}
                buildVaccinationDisplayEntries={buildVaccinationDisplayEntries}
                renderEvidenceExpirationCell={renderEvidenceExpirationCell}
                renderEvidenceSubmittedDetailsCell={renderEvidenceSubmittedDetailsCell}
                openProtectedEvidenceFile={openProtectedEvidenceFile}
                setVaccinationCardViewItem={setVaccinationCardViewItem}
                setConfirmMessage={setConfirmMessage}
                setConfirmAction={setConfirmAction}
                setShowConfirmModal={setShowConfirmModal}
                reviewOnboardingDocumentSubmission={reviewOnboardingDocumentSubmission}
                openDeleteEvidenceConfirm={openDeleteEvidenceConfirm}
                reviewEvidence={reviewEvidence}
                requestRejectEvidence={requestRejectEvidence}
              />
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>

      <VaccinationCardViewModal
        show={!!vaccinationCardViewItem}
        onHide={() => setVaccinationCardViewItem(null)}
        details={vaccinationCardViewItem?.details}
        canEditDoses={true}
        onSaveDetails={(nextDetails) =>
          updateVaccinationCardEvidenceDetails(vaccinationCardViewItem?.id, nextDetails)
        }
        residentName={
          vaccinationCardViewItem?.resident
            ? `${vaccinationCardViewItem.resident.firstName || ''} ${vaccinationCardViewItem.resident.lastName || ''}`.trim()
            : ''
        }
      />

      {/* Promote Individual Resident Modal */}
      <Modal show={showPromoteModal} onHide={() => setShowPromoteModal(false)}>
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-100">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>Promote Resident</span>
              <div className="text-hl"></div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          {selectedResident && (
            <div>
              <p>
                <strong>Resident:</strong> {selectedResident.firstName} {selectedResident.lastName}
              </p>
              <p>
                <strong>Current PGY:</strong> {selectedResident.year} → {selectedResident.year + 1}
              </p>
              <Form.Group>
                <FloatingLabel controlId="floatingInput" label="Promotion Date" className="required-field">
                <Form.Control
                  type="date"
                  value={promotionDate}
                  onChange={(e) => setPromotionDate(e.target.value)}
                  max={moment().format('YYYY-MM-DD')}
                />
                </FloatingLabel>
                <Form.Text className="text-muted">
                  Select today or a date in the past. Future promotions are not allowed.
                </Form.Text>
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="px-4 border-top-0">
          <Button variant="light" size="sm" onClick={() => setShowPromoteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            size="sm"
            onClick={() => promoteResident(selectedResident.id)}
            disabled={promoting || moment(promotionDate).isAfter(moment().startOf('day'))}
          >
            {promoting ? <Spinner animation="border" size="sm" /> : 'Promote'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Graduate Resident Modal */}
      <Modal show={showGraduateModal} onHide={() => setShowGraduateModal(false)}>
        {console.log('🎓 Graduation modal state:', { showGraduateModal, selectedResident })}
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-100">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>Graduate Resident</span>
              <div className="text-hl"></div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          {selectedResident && (
            <div>
              <p>
                <strong>Resident:</strong> {selectedResident.firstName} {selectedResident.lastName}
              </p>
              <p>
                <strong>Current Status:</strong> PGY-{selectedResident.year} → Graduated
              </p>
              <p className="text-warning">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>Warning:</strong> This action will deactivate the resident's account and mark them as graduated.
              </p>
              <Form.Group>
                <FloatingLabel controlId="floatingInput" label="Graduation Date" className="required-field">
                <Form.Control
                  type="date"
                  value={promotionDate}
                  onChange={(e) => setPromotionDate(e.target.value)}
                  max={moment().format('YYYY-MM-DD')}
                />
                </FloatingLabel>
                <Form.Text className="text-muted">
                  Select today or a date in the past. Future graduations are not allowed.
                </Form.Text>
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="px-4 border-top-0">
          <Button variant="light" size="sm" onClick={() => setShowGraduateModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="success" 
            size="sm"
            onClick={graduateResident}
            disabled={promoting || moment(promotionDate).isAfter(moment().startOf('day'))}
          >
            {promoting ? <Spinner animation="border" size="sm" /> : 'Graduate'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Promotion History Modal */}
      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg">
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-100">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>Promotion History</span>
              <div className="text-hl"></div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          {selectedResident && (
            <div>
              <h6>{selectedResident.firstName} {selectedResident.lastName}</h6>
              <p className="text-muted">Current PGY: {selectedResident.year}</p>
              
              {selectedResident.pgyHistory && selectedResident.pgyHistory.length > 0 ? (
                <Table responsive>
                  <thead>
                    <tr>
                      <th>PGY Level</th>
                      <th>Academic Year</th>
                      <th>Promotion Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedResident.pgyHistory.map((history, index) => (
                      <tr key={index}>
                        <td>
                          <Badge bg={getPGYBadgeColor(history.pgyLevel)}>
                            PGY-{history.pgyLevel}
                          </Badge>
                        </td>
                        <td>
                          {moment(history.academicYearStart).format('YYYY')}-{moment(history.academicYearEnd).format('YYYY')}
                        </td>
                        <td>{moment(history.promotionDate).format('MMM D, YYYY')}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted">No promotion history available.</p>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Backtrack Promotion Modal */}
      <Modal show={showBacktrackModal} onHide={() => setShowBacktrackModal(false)}>
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-100">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>Backtrack Promotion</span>
              <div className="text-hl"></div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          {selectedResident && (
            <div>
              <Alert variant="warning">
                <strong>Warning:</strong> This action will revert the resident's most recent promotion and cannot be undone.
              </Alert>
              <p>
                <strong>Resident:</strong> {selectedResident.firstName} {selectedResident.lastName}
              </p>
              <p>
                <strong>Current PGY:</strong> {selectedResident.year} → {selectedResident.year - 1}
              </p>
              <p>
                <strong>Action:</strong> Remove the most recent promotion from the resident's history and restore their previous PGY level.
              </p>
              <p className="text-muted small">
                This will update the resident's current PGY level, academic year start date, graduation year, and last promotion date to their previous values.
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="px-4 border-top-0">
          <Button variant="light" size="sm" onClick={() => setShowBacktrackModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="warning" 
            onClick={handleBacktrackPromotion}
            disabled={backtracking}
          >
            {backtracking ? <Spinner animation="border" size="sm" /> : 'Backtrack Promotion'}
          </Button>
        </Modal.Footer>
      </Modal>

      {renderEligibilityModal()}
      {renderCopyRequirementsModal()}
      {renderRequirementsModal()}
      {renderInServiceModal()}

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-100">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>Confirm Action</span>
              <div className="text-hl"></div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          {confirmMessage}
        </Modal.Body>
        <Modal.Footer className="px-4 border-top-0">
          <Button variant="light" size="sm" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={() => { setShowConfirmModal(false); confirmAction(); }}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showRejectReasonModal}
        onHide={() => {
          setShowRejectReasonModal(false);
          setRejectTargetEvidence(null);
          setRejectReasonText('');
        }}
        centered
      >
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-100">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>Reject Submission</span>
              <div className="text-hl"></div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          <div className="small mb-2">
            Provide a reason for rejection. Residents will see this in their Promotion Requirements checklist.
          </div>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">Reason for rejection</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={rejectReasonText}
              onChange={(e) => setRejectReasonText(e.target.value)}
              placeholder="Type reason..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="px-4 border-top-0">
          <Button
            variant="light"
            size="sm"
            onClick={() => {
              setShowRejectReasonModal(false);
              setRejectTargetEvidence(null);
              setRejectReasonText('');
            }}
          >
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={submitRejectEvidenceWithReason}>
            Reject with reason
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ResidentPromotionManager; 